import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER, LOCALE_ID, ElementRef, Type } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { UpgradeModule } from '@angular/upgrade/static';
import { default as imAppModule } from '../ng-app/app.module.ajs';
import { UIRouterUpgradeModule } from '@uirouter/angular-hybrid';

import { MicrosoftGraphModule } from './modules/microsoft-graph/microsoft-graph.module';

import { AppComponent } from './app.component';
import { AppConfigModule } from './appconfig.module';
import { AppConfigService } from './services';
import { GraphAuthService } from './modules/microsoft-graph/auth/services/graph-auth.service';
import { CalendarService } from './modules/microsoft-graph/services/graph-calendar.service';
import { GraphUserService } from './modules/microsoft-graph/services/graph-user.service';
import { BjsTestComponent } from './views/components/bjs-test/bjs-test.component';
import { GoogleTranslateService } from './services/google-translate.service';
import { DataTaskService } from './core/services/data-task.service';
import { GraphMailService } from './modules/microsoft-graph/services/graph-mail.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatDialogModule } from '@angular/material/dialog';
import { WordlangDialogComponent } from './modal/wordlang-dialog/wordlang-dialog.component';
import { TtInputComponent } from './core/components/tt-input/tt-input.component';
import { GridstackModule, GridstackComponent } from 'gridstack/dist/angular';
import { AgChartsAngularModule } from 'ag-charts-angular';
import { TtListLegacyModule } from './core/components/tt-list-legacy/tt-list-legacy.module';

/**
 * IMPORTANT: RxDB creates rxjs observables outside of angulars zone
 * So you have to import the rxjs patch to ensure changedetection works correctly.
 * @link https://www.bennadel.com/blog/3448-binding-rxjs-observable-sources-outside-of-the-ngzone-in-angular-6-0-2.htm
 */
import 'zone.js/plugins/zone-patch-rxjs';
import { CardComponent } from './core/components/card/card.component';
import { PageComponent } from './core/components/page/page.component';
import { JlrTestComponent } from './views/components/jlr-test/jlr-test.component';
import { ResponsiveBreakpointsComponent } from './core/components/responsive-breakpoints/responsive-breakpoints.component';
import { ResponsiveService } from './core/services/responsive.service';
import { ResponsiveBridgeService } from './core/services/responsive-bridge.service';
import { NgChartsModule } from 'ng2-charts';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UserService } from './core/services/user.service';
import { UserStore } from './core/services/user.store';
import { AppSettingsService } from './core/services/app-settings.service';
import { OutsideClickDirective } from './core/directives/outside-click/outside-click.directive';
import { InputNumberComponent } from './core/components/input-number/input-number.component';
import { FormFieldBaseComponent } from './core/components/form-field-base/form-field-base.component';
import { FormFieldButtonsComponent } from './core/components/form-field-buttons/form-field-buttons.component';
import { CheckboxComponent } from './core/components/checkbox/checkbox.component';
import { ListboxComponent, ListboxHighlight } from './core/components/listbox/listbox.component';
import { ListboxPopupDirective } from './core/directives/listbox-popup/listbox-popup.directive';
import { SearchComponent } from './core/components/search/search.component';
import { FormFieldButtonComponent } from './core/components/form-field-button/form-field-button.component';
import { ComponentBaseComponent } from './core/components/component-base/component-base.component';
import { TabsComponent } from './core/components/tabs/tabs.component';
import { DatetimeComponent } from './core/components/datetime/datetime.component';
import { MatInputModule, MatInput } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { ContextMenuContainerComponent } from './core/components/context-menu/context-menu-container/context-menu-container.component';
import { ContextMenuComponent } from './core/components/context-menu/context-menu/context-menu.component';
import { ContextMenuItemComponent } from './core/components/context-menu/context-menu-item/context-menu-item.component';
import { MatCalendar, MatCalendarHeader, MatDatepickerModule } from '@angular/material/datepicker';
import { MatTable, MatTableModule } from '@angular/material/table';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_CHIPS_DEFAULT_OPTIONS, MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { PasswordInputComponent } from './core/components/password-input/password-input.component';
import { MultiSelectComponent, MutliSelectFilter } from './core/components/multi-select/multi-select.component';
import { TooltipPosition, MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, MatPaginatorIntl } from '@angular/material/paginator';
import { FieldGroupComponent } from './core/components/field-group/field-group.component';
import { OverflowMenuComponent } from './core/components/overflow-menu/overflow-menu.component';
import { OverflowMenuItemComponent } from './core/components/overflow-menu/overflow-menu-item/overflow-menu-item.component';
import { PageHeadingComponent } from './core/components/typography/page-heading/page-heading.component';
import { AgGridModule } from '@ag-grid-community/angular';
import { GridComponent } from './core/components/grid/grid.component';
import { GridLoadingOverlayComponent } from './core/components/grid/grid-loading-overlay/grid-loading-overlay.component';
import { DateTimeCellEditorComponent } from './core/components/grid/cell-editors/date-time-cell-editor/date-time-cell-editor.component';
import { LookupCellEditorComponent } from './core/components/grid/cell-editors/lookup-cell-editor/lookup-cell-editor.component';
import { NumberCellEditorComponent } from './core/components/grid/cell-editors/number-cell-editor/number-cell-editor.component';
import { CheckboxFloatingFilterComponent } from './core/components/grid/floating-filters/checkbox-floating-filter/checkbox-floating-filter.component';
import { SymbolCellRendererComponent } from './core/components/grid/cell-renderers/symbol-cell-renderer/symbol-cell-renderer.component';
import { ColumnLayoutModalComponent } from './core/components/grid/modals/column-layout-modal/column-layout-modal.component';
import { FileUploaderComponent } from './core/components/file-uploader/file-uploader.component';
import { GridFunctionsCellRendererComponent } from './core/components/grid/cell-renderers/grid-functions-cell-renderer/grid-functions-cell-renderer.component';
import { SelectComponent } from './core/components/select/select.component';
import { SelectOptionComponent } from './core/components/select/select-option.component';
import { TextareaComponent } from './core/components/textarea/textarea.component';
import { ButtonComponent } from './core/components/button/button.component';
import { PrintModalComponent } from './core/components/grid/modals/print-modal/print-modal.component';
import { registerLocaleData } from '@angular/common';
import LocaleZh from '@angular/common/locales/zh';
import LocalePl from '@angular/common/locales/pl';
import LocaleIt from '@angular/common/locales/it';
import LocaleFr from '@angular/common/locales/fr';
import LocaleLt from '@angular/common/locales/lt';
import LocaleFi from '@angular/common/locales/fi';
import LocaleEs from '@angular/common/locales/es';
import LocaleDe from '@angular/common/locales/de';
import LocaleDa from '@angular/common/locales/da';
import LocaleSe from '@angular/common/locales/se';
import LocaleNo from '@angular/common/locales/no';
// import LocaleNo from '@angular/common/locales/no';
// import LocaleNo from '@angular/common/locales/no';
import { ConfirmModalComponent } from './core/components/grid/modals/confirm-modal/confirm-modal.component';
import { LinkCellRendererComponent } from './core/components/grid/cell-renderers/link-cell-renderer/link-cell-renderer.component';
import { ProgressBarComponent } from './core/components/progress-bar/progress-bar.component';
import { ProgressModalComponent } from './core/components/grid/modals/progress-modal/progress-modal.component';
registerLocaleData(LocaleZh);
registerLocaleData(LocalePl);
registerLocaleData(LocaleIt);
registerLocaleData(LocaleFr);
registerLocaleData(LocaleLt);
registerLocaleData(LocaleFi);
registerLocaleData(LocaleEs);
registerLocaleData(LocaleDe);
registerLocaleData(LocaleDa);
registerLocaleData(LocaleSe);
registerLocaleData(LocaleNo);
import { ButtonSaveComponent } from './core/components/button-save/button-save.component';
import { ButtonMultiComponent } from './core/components/button-multi/button-multi.component';
import { PopoverComponent } from './core/components/popover/popover.component';
import { PopoverDirective } from './core/components/popover/popover.directive';
import { InputComponent } from './core/components/input/input.component';
import { CheckboxColumnHeaderComponent } from './core/components/grid/column-headers/checkbox-column-header/checkbox-column-header.component';
import { ThumbnailCellRendererComponent } from './core/components/grid/cell-renderers/thumbnail-cell-renderer/thumbnail-cell-renderer.component';
import { RowEditModalComponent } from './core/components/grid/modals/row-edit-modal/row-edit-modal.component';
import { PrintService } from './core/services/print.service';
import { PopupTableComponent } from './core/components/grid/modals/popup-table/popup-table.component';
import { DateCellEditorComponent } from './core/components/grid/cell-editors/date-cell-editor/date-cell-editor.component';
import { DATE_FORMATS, TTDateAdapter } from './core/models/date-adapter';
import { DateFloatingFilterComponent } from './core/components/grid/floating-filters/date-floating-filter/date-floating-filter.component';
import { DateFilterSelectionComponent } from './core/components/grid/filters/date-filter-selection/date-filter-selection.component';
import { ErrorModalComponent } from './core/components/grid/modals/error-modal/error-modal.component';
import { MessageDialogComponent } from './core/services/modal.service';
import { GdprDialogComponent, MainMenuComponent } from './views/components/mainmenu/mainmenu.component';
import { BankReconciliationComponent, BankReconciliationFilter } from './views/components/bank-reconciliation/bank-reconciliation.component';
import { BankReconciliationItemComponent } from './views/components/bank-reconciliation/bank-reconciliation-item/bank-reconciliation-item.component';
import { GridBookingComponent } from './modal/grid-booking/grid-booking.component';
import { StudentTestComponent } from './views/components/student-test/student-test.component';
import { KanbanComponent } from './core/components/kanban/kanban.component';
import { ModalService } from './core/services/modal.service';
import { SignaturePadModalComponent } from './core/components/grid/modals/signature-pad-modal/signature-pad-modal.component';
import { SignaturePadComponent } from './core/components/signature-pad/signature-pad.component';
import { MyActivitiesComponent } from './views/components/my-activities/my-activities.component';
import { TtInputButtonsDirective } from './core/directives/tt-input-buttons/tt-input-buttons.directive';
import { TtListDirective } from './core/directives/tt-list/tt-list.directive';
import { ImInputDirective } from './core/directives/im-input/im-input.directive';
import { SettingsComponent } from './views/components/settings/settings.component';
import { HtmlReportComponent } from './core/components/html-report/html-report.component';
import { HtmlReportService } from './services/html-report.service';
import { ContextMenuReportComponent } from './core/components/context-menu-report/context-menu-report.component';
import { HtmlProcEditorComponent } from './core/components/html-proc-editor/html-proc-editor.component';
import { AuthInterceptor } from './core/interceptors/auth-interceptor';
import { CommonInterceptor } from './core/interceptors/common-interceptor';
import { SirvSetupComponent } from './views/components/sirv-setup/sirv-setup.component';
import { PictureBankComponent } from './views/components/picture-bank/picture-bank.component';
import { PictureBanksComponent } from './views/components/picture-banks/picture-banks.component';
import { DateInputComponent } from './core/components/date-input/date-input.component';
import { DateComponent } from './core/components/date/date.component';
import { ListItemComponent } from './core/components/list/list-item/list-item.component';
import { ListComponent } from './core/components/list/list.component';
import { ListFilterPipe } from './core/components/list/pipes/list-filter.pipe';
import { ListSortPipe } from './core/components/list/pipes/list-sort.pipe';
import { ListFilterDirective } from './core/components/list/directives/list-filter.directive';
import { ListCheckboxSelectionDirective } from './core/components/list/directives/list-checkbox-selection.directive';
import { ListRadioSelectionDirective } from './core/components/list/directives/list-radio-selection.directive';
import { TimeInputComponent } from './core/components/time-input/time-input.component';
import { TimeComponent } from './core/components/time/time.component';
import { ButtonGotoComponent } from './core/components/button-goto/button-goto.component';
import { TabComponent } from './core/components/tabs/tab/tab.component';
import { ButtonSettingsComponent } from './core/components/button-settings/button-settings.component';
import { ImageComponent } from './core/components/image/image.component';
import { ContentSwitcherComponent } from './core/components/content-switcher/content-switcher.component';
import { SwitchComponent } from './core/components/content-switcher/switch/switch.component';
import { DateRangeComponent } from './core/components/date-range/date-range.component';
import { DividerComponent } from './core/components/divider/divider.component';
import { DividerRowsComponent } from './core/components/divider/divider-rows/divider-rows.component';
import { DividerColumnsComponent } from './core/components/divider/divider-columns/divider-columns.component';
import { DividerPaneComponent } from './core/components/divider/divider-pane/divider-pane.component';
import { BusinesscoSettingSmtpComponent } from './views/components/businessco-setting-smtp/businessco-setting-smtp.component';
import { WebpagemenuComponent } from './core/components/webpagemenu/webpagemenu.component';
import { WebpagemenuGroupWidgetComponent } from './core/components/webpagemenu/webpagemenu-group-widget/webpagemenu-group-widget.component';
import { EconomyMenuComponent } from './views/components/economy-menu/economy-menu.component';
import { ProductionMenuComponent } from './views/components/production-menu/production-menu.component';
import { CrmMenuComponent } from './views/components/crm-menu/crm-menu.component';
import { ConsignmentMenuComponent } from './views/components/consignment-menu/consignment-menu.component';
import { StockMenuComponent } from './views/components/stock-menu/stock-menu.component';
import { ProductMenuComponent } from './views/components/product-menu/product-menu.component';
import { PurchaseMenuComponent } from './views/components/purchase-menu/purchase-menu.component';
import { EcommerceMenuComponent } from './views/components/ecommerce-menu/ecommerce-menu.component';
import { TimestampMenuComponent } from './views/components/timestamp-menu/timestamp-menu.component';
import { ExternalProductionMenuComponent } from './views/components/external-production-menu/external-production-menu.component';
import { ToolsMenuComponent } from './views/components/tools-menu/tools-menu.component';
import { DocarticleMenuComponent } from './views/components/docarticle-menu/docarticle-menu.component';
import { MyPageComponent } from './views/components/my-page/my-page.component';
import { DevelopMenuComponent } from './views/components/develop-menu/develop-menu.component';
import { HrMenuComponent } from './views/components/hr-menu/hr-menu.component';
import { ChangePasswordDialogComponent } from './modal/change-password-dialog/change-password-dialog.component';
import { TranslateWordDirective } from './core/directives/translate.directive';
import { WebpagemenuDashboardComponent } from './core/components/webpagemenu/webpagemenu-dashboard/webpagemenu-dashboard.component';
import { WebpagemenuDashboardWidgetComponent } from './core/components/webpagemenu/webpagemenu-dashboard/webpagemenu-dashboard-widget/webpagemenu-dashboard-widget.component';
import { P2DatataskLogsComponent } from './views/components/p2-datatask-logs/p2-datatask-logs.component';
import { P2DatataskLogComponent, PrettyJsonPipe } from './views/components/p2-datatask-log/p2-datatask-log.component';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CheckboxListComponent } from './core/components/list/checkbox-list/checkbox-list.component';
import { DispatchPlanningComponent } from './views/components/dispatch-planning/dispatch-planning.component';
import { ColorInputComponent } from './core/components/color-input/color-input.component';
import { CdkScrollableModule, ScrollingModule } from '@angular/cdk/scrolling';
import { DashboardWidgetBaseComponent } from './core/components/webpagemenu/webpagemenu-dashboard/dashboard-widget-base/dashboard-widget-base.component';
import { DashboardWidgetBasicComponent } from './core/components/webpagemenu/webpagemenu-dashboard/dashboard-widget-basic/dashboard-widget-basic.component';
import { DashboardWidgetChartComponent } from './core/components/webpagemenu/webpagemenu-dashboard/dashboard-widget-chart/dashboard-widget-chart.component';
import { DashboardWidgetGridComponent } from './core/components/webpagemenu/webpagemenu-dashboard/dashboard-widget-grid/dashboard-widget-grid.component';
import { DashboardChartModalComponent } from './core/components/webpagemenu/webpagemenu-dashboard/dashboard-chart-modal/dashboard-chart-modal.component';
import { KpiCompComponent } from './views/components/kpi-comp/kpi-comp.component';
import { SaftExportComponent } from './views/components/saft-export/saft-export.component';
import { TranslateEditComponent } from './core/components/translate-edit/translate-edit.component';
import { DeviationSettingsComponent } from './views/components/deviation-settings/deviation-settings.component';
import { DeviationTypeComponent } from './views/components/deviation-type/deviation-type.component';
import { DeviationSourceComponent } from './views/components/deviation-source/deviation-source.component';
import { TranslateEditDialogComponent } from './modal/translate-edit-dialog/translate-edit-dialog.component';
import { DeviationSource2Component } from './views/components/deviation-source2/deviation-source2.component';
import { DeviationSource2ViewComponent } from './views/components/deviation-source2-view/deviation-source2-view.component';
import { DeviationSourceViewComponent } from './views/components/deviation-source-view/deviation-source-view.component';
import { DeviationTypeViewComponent } from './views/components/deviation-type-view/deviation-type-view.component';
import { CalculatorModalComponent } from './core/components/calculator-modal/calculator-modal.component';
import { ProductSettingMcItemComponent } from './views/components/product-setting-mc-item/product-setting-mc-item.component';
import { P2UpdateScriptEditComponent } from './views/components/p2-update-script-edit/p2-update-script-edit.component';
import { P2UpdateScriptEditViewComponent } from './views/components/p2-update-script-edit-view/p2-update-script-edit-view.component';
import { P2UpdateScriptsComponent } from './views/components/p2-update-scripts/p2-update-scripts.component';
import { P2UpdateScriptEditDialogComponent } from './modal/p2-update-script-edit-dialog/p2-update-script-edit-dialog.component';
import { TtNumpadLegacyComponent } from './core/components/tt-numpad-legacy/tt-numpad-legacy.component';
import { TtInputLegacyComponent } from './core/components/tt-input-legacy/tt-input-legacy.component';
import { ConsignmentPlansDeliveryComponent } from './views/components/consignment-plans-delivery/consignment-plans-delivery.component';
import { SliderComponent } from './core/components/slider/slider.component';
import { ToastComponent } from './core/components/toast/toast.component';
import { AccountingBsComponent } from './views/components/accounting-bs/accounting-bs.component';
import { BottomSheetComponent } from './core/components/bottom-sheet/bottom-sheet.component';
import { DashboardSlicerWidgetBaseComponent } from './core/components/webpagemenu/webpagemenu-dashboard/slicers/dashboard-slicer-widget-base/dashboard-slicer-widget-base.component';
import { EditorModule } from '@progress/kendo-angular-editor';
import { POPUP_CONTAINER, PopupModule } from '@progress/kendo-angular-popup';
import { KendoBaseComponent } from './core/components/kendo-base/kendo-base.component';
import { EditorComponent } from './core/components/editor/editor.component';
import { PurchReceivalPlanningComponent } from './views/components/purch-receival-planning/purch-receival-planning.component';
import { ConsignmentPlansConsignmentsComponent } from './views/components/consignment-plans-consignments/consignment-plans-consignments.component';
import { TtScreenBlockComponent } from './core/components/tt-screen-block/tt-screen-block.component';
import { DocArticleEditComponent } from './views/components/doc-article-edit/doc-article-edit.component';
import { CommentComponent } from './core/components/comment/comment.component';
import { ChatComponent } from './core/components/chat/chat.component';
import { CustactDialogsComponent } from './views/components/custact-dialogs/custact-dialogs.component';
import { PickJobGroupComponent } from './views/components/pick-job-group/pick-job-group.component';
import { LabelTranslationDialogComponent } from './modal/label-translation-dialog/label-translation-dialog.component';
import { InterbranchEquipmentInvoicingComponent } from './views/components/interbranch-equipment-invoicing/interbranch-equipment-invoicing.component';
import { ChecklistItemComponent } from './views/components/checklist-item/checklist-item.component';
import { PlannerComponent, PlannerResourceFilter } from './core/components/planner/planner.component';
import { PlannerEventComponent } from './core/components/planner/planner-event/planner-event.component';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { HelpdocComponent } from './views/components/helpdoc/helpdoc.component';
import { VideoTutorialsComponent } from './views/components/video-tutorials/video-tutorials.component';
import { VideoTutorialComponent } from './views/components/video-tutorial/video-tutorial.component';
import { MetaColumnItemComponent } from './views/components/meta-column-item/meta-column-item.component';
import { AccountingProcessingComponent } from './views/components/accounting-processing/accounting-processing.component';
import { AccountingProcessingHeadComponent } from './views/components/accounting-processing/accounting-processing-head/accounting-processing-head.component';
import { ProductsComponent } from './views/components/products/products.component';
import { YouTubePlayer, YouTubePlayerModule } from '@angular/youtube-player';

import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { SpreadsheetModule } from '@progress/kendo-angular-spreadsheet';
import { SchedulerModule } from '@progress/kendo-angular-scheduler';
import { TreeViewComponent } from './core/components/tree-view/tree-view.component';
import { ProductBomComponent } from './views/components/product-bom/product-bom.component';
import { MediaQueryPipe } from './core/pipe/media-query.pipe';
import { EmailComponent } from './views/components/email/email.component';
import { EmailArchiveFormComponent } from './views/components/email/email-archive-form/email-archive-form.component';
import { ConcernOrdersGridComponent } from './views/components/concern-orders-grid/concern-orders-grid.component';
import { DocumentViewerLegacyDirective } from './core/directives/document-viewer-legacy.directive';
import { DocumentViewerComponent } from './core/components/document-viewer/document-viewer.component';
import { OfficeDocumentViewerComponent } from './core/components/document-viewer/office-document-viewer/office-document-viewer.component';
import { EmailViewerComponent } from './core/components/document-viewer/email-viewer/email-viewer.component';
import { EmailViewComponent } from './views/components/email-view/email-view.component';
import { SearchMultiSelect } from './core/components/search-multi-select/search-multi-select.component';
import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { SideDrawerContainerComponent } from './core/components/side-drawer-container/side-drawer-container.component';
import { SideDrawerComponent } from './core/components/side-drawer-container/side-drawer/side-drawer.component';
import { OrderheadExternalCostComponent } from './views/components/orderhead-external-cost/orderhead-external-cost.component';
import { SpreadsheetComponent } from './core/components/spreadsheet/spreadsheet.component';
import { PurchPlanningOverviewComponent } from './views/components/purch-planning-overview/purch-planning-overview.component';
import { FormatDatePipe } from './core/pipe/format-date.pipe';
import { ProductsOverviewComponent } from './views/components/products-overview/products-overview.component';
import { AccountproductComponent } from './views/components/accountproduct/accountproduct.component';
import { OrderPurchComponent } from './views/components/order-purch/order-purch.component';
import { TravelWageMenuComponent } from './views/components/travel-wage-menu/travel-wage-menu.component';
import { EditDocarcDocumentCategoryDialogComponent } from './modal/edit-docarc-document-category-dialog/edit-docarc-document-category-dialog.component';
import { DocumentViewerGridDialogComponent } from './modal/document-viewer-grid-dialog/document-viewer-grid-dialog.component';
import { DeviantVatComponent } from './views/components/deviant-vat/deviant-vat.component';
import { DocumentsComponent } from './views/components/documents/documents.component';
import { CustactCalcComponent } from './views/components/custact-calc/custact-calc.component';
import { AboutComponent } from './views/components/about/about.component';
import { AboutViewComponent } from './views/components/about-view/about-view.component';
import { GridDrillDownComponent } from './core/components/grid/modals/grid-drill-down/grid-drill-down.component';
import { PrognosisPurchaseOverviewComponent } from './views/components/prognosis-purchase-overview/prognosis-purchase-overview.component';
import { P2SettingEditFormComponent } from './views/components/p2-setting-edit-form/p2-setting-edit-form.component';
import { P2SettingEditViewComponent } from './views/components/p2-setting-edit-view/p2-setting-edit-view.component';
import { P2SettingsComponent } from './views/components/p2-settings/p2-settings.component';
import { ProductsUnitTooltipComponent } from './modal/products-unit-tooltip/products-unit-tooltip.component';
import { GridHtmlTooltipComponent } from './core/components/grid/tooltips/grid-html-tooltip/grid-html-tooltip.component';
import { DocArticlesComponent } from './views/components/doc-articles/doc-articles.component';
import { DocArticleComponent } from './views/components/doc-article/doc-article.component';
import { DocArticleEditViewComponent } from './views/components/doc-article-edit-view/doc-article-edit-view.component';
import { DocArticleViewComponent } from './views/components/doc-article-view/doc-article-view.component';
import { AddExistingDocArcComponent } from './views/components/add-existing-doc-arc/add-existing-doc-arc.component';
import { AddExistingDocArcDialogComponent } from './modal/add-existing-doc-arc-dialog/add-existing-doc-arc-dialog.component';
import { ActStatusesComponent } from './views/components/act-statuses/act-statuses.component';
import { ActivityTypeActStatusesComponent } from './views/components/activity-type-act-statuses/activity-type-act-statuses.component';
import { ActStatusMenuComponent } from './views/components/act-status-menu/act-status-menu.component';
import { ActivityTypeActStatusComponent } from './views/components/activity-type-act-status/activity-type-act-status.component';
import { ActivityTypeLangsComponent } from './views/components/activity-type-langs/activity-type-langs.component';
import { ActStatusLangsComponent } from './views/components/act-status-langs/act-status-langs.component';
import { ActivityTypesComponent } from './views/components/activity-types/activity-types.component';
import { ActivityTypeDevTypesComponent } from './views/components/activity-type-dev-types/activity-type-dev-types.component';
import { PrognosisPurchaseHistoryComponent } from './views/components/prognosis-purchase-history/prognosis-purchase-history.component';
import { FieldContainerComponent } from './core/components/field-container/field-container.component';
import { OrderDialogDialogComponent } from './modal/order-dialog-dialog/order-dialog-dialog.component';
import { OrderHeadDynamicSpecialComponent } from './views/components/order-head-dynamic-special/order-head-dynamic-special.component';
import { ActivityTypeDevTypeComponent } from './views/components/activity-type-dev-type/activity-type-dev-type.component';
import { OrderheadDialogComponent } from './views/components/orderhead-dialog/orderhead-dialog.component';
import { AcReportLayoutsComponent } from './views/components/ac-report-layouts/ac-report-layouts.component';
import { AcReportLayoutComponent } from './views/components/ac-report-layout/ac-report-layout.component';
import { AcReportLayoutLineComponent } from './views/components/ac-report-layout-line/ac-report-layout-line.component';
import { BusinesscoSettingsComponent } from './views/components/businessco-settings/businessco-settings.component';
import { BusinessSitesComponent } from './views/components/business-sites/business-sites.component';
import { BusinessSiteComponent } from './views/components/business-site/business-site.component';
import { StockPlacesToComponent } from './views/components/stock-places-to/stock-places-to.component';
import { StockPlaceDelTimeComponent } from './views/components/stock-place-del-time/stock-place-del-time.component';
import { PickJobGrpsComponent } from './views/components/pick-job-grps/pick-job-grps.component';
import { TransportAreasComponent } from './views/components/transport-areas/transport-areas.component';
import { TransportAreaMenuComponent } from './views/components/transport-area-menu/transport-area-menu.component';
import { TransportAreaComponent } from './views/components/transport-area/transport-area.component';
import { TransportAreaPpAddComponent } from './views/components/transport-area-pp-add/transport-area-pp-add.component';
import { ProjectComponent } from './views/components/project/project.component';
import { ProjectViewComponent } from './views/components/project-view/project-view.component';
import { StockPlaceDelTimesComponent } from './views/components/stock-place-del-times/stock-place-del-times.component';
import { CategoryListMainComponent } from './views/components/category-list-main/category-list-main.component';
import { OrderLineCreditCausesComponent } from './views/components/order-line-credit-causes/order-line-credit-causes.component';
import { OrderLineCreditCauseComponent } from './views/components/order-line-credit-cause/order-line-credit-cause.component';
import { OrderLineCreditCauseLangComponent } from './views/components/order-line-credit-cause-lang/order-line-credit-cause-lang.component';
import { TransportAreaPpComponent } from './views/components/transport-area-pp/transport-area-pp.component';
import { ProjectMenuComponent } from './views/components/project-menu/project-menu.component';
import { ProjectsComponent } from './views/components/projects/projects.component';
import { ProjectMenuViewComponent } from './views/components/project-menu-view/project-menu-view.component';
import { StateModalComponent } from './modal/state-modal/state-modal.component';
import { BaseCurrenciesComponent } from './views/components/base-currencies/base-currencies.component';
import { CurrencyTypesComponent } from './views/components/currency-types/currency-types.component';
import { CurrencyExRatesComponent } from './views/components/currency-ex-rates/currency-ex-rates.component';
import { CurrencyExRateHistComponent } from './views/components/currency-ex-rate-hist/currency-ex-rate-hist.component';
import { CurrencyExRateComponent } from './views/components/currency-ex-rate/currency-ex-rate.component';
import { CustGrpsComponent } from './views/components/cust-grps/cust-grps.component';
import { CustGrpMenuComponent } from './views/components/cust-grp-menu/cust-grp-menu.component';
import { CustGrpLangsComponent } from './views/components/cust-grp-langs/cust-grp-langs.component';
import { ValutaComponent } from './views/components/valuta/valuta.component';
import { CustomsTarifsComponent } from './views/components/customs-tarifs/customs-tarifs.component';
import { CustomsTarifMenuComponent } from './views/components/customs-tarif-menu/customs-tarif-menu.component';
import { CustomsTarifLangComponent } from './views/components/customs-tarif-lang/customs-tarif-lang.component';
import { CustomsTarifComponent } from './views/components/customs-tarif/customs-tarif.component';
import { DbStationComponent } from './views/components/db-station/db-station.component';
import { StockPlacesComponent } from './views/components/stock-places/stock-places.component';
import { DeliveryMethodsComponent } from './views/components/delivery-methods/delivery-methods.component';
import { DeliveryMethodServicesComponent } from './views/components/delivery-method-services/delivery-method-services.component';
import { TranscarrierServiceActiveComponent } from './views/components/transcarrier-service-active/transcarrier-service-active.component';
import { DeliveryMethodLangsComponent } from './views/components/delivery-method-langs/delivery-method-langs.component';
import { DeliveryTermsComponent } from './views/components/delivery-terms/delivery-terms.component';
import { DeliveryTermComponent } from './views/components/delivery-term/delivery-term.component';
import { DeliveryTermLangsComponent } from './views/components/delivery-term-langs/delivery-term-langs.component';
import { SideNavComponent, SideNavFilterPipe } from './core/components/side-nav/side-nav.component';
import { SideNavItemComponent } from './core/components/side-nav/side-nav-item/side-nav-item.component';
import { MSAL_INSTANCE, MsalModule, MsalService } from '@azure/msal-angular';
import { PublicClientApplication } from '@azure/msal-browser';
import { NavBarComponent } from './core/components/nav-bar/nav-bar.component';
import { FavouritesDialogComponent } from './modal/favourites-dialog/favourites-dialog.component';
import { DepartmentsComponent } from './views/components/departments/departments.component';
import { DepartmentMenuComponent } from './views/components/department-menu/department-menu.component';
import { DepartmentLangComponent } from './views/components/department-lang/department-lang.component';
import { DiscGrpCustsComponent } from './views/components/disc-grp-custs/disc-grp-custs.component';
import { DiscGrpCustComponent } from './views/components/disc-grp-cust/disc-grp-cust.component';
import { DiscGrpCustLangsComponent } from './views/components/disc-grp-cust-langs/disc-grp-cust-langs.component';
import { DiscGrpProdsComponent } from './views/components/disc-grp-prods/disc-grp-prods.component';
import { DiscGrpProdComponent } from './views/components/disc-grp-prod/disc-grp-prod.component';
import { DiscGrpProdLangsComponent } from './views/components/disc-grp-prod-langs/disc-grp-prod-langs.component';
import { DiscGrpDatasComponent } from './views/components/disc-grp-datas/disc-grp-datas.component';
import { DocCategoriesComponent } from './views/components/doc-categories/doc-categories.component';
import { DocCategoryComponent } from './views/components/doc-category/doc-category.component';
import { DocCategoryLangComponent } from './views/components/doc-category-lang/doc-category-lang.component';
import { DoDataImpsWizardComponent } from './views/components/do-data-imps-wizard/do-data-imps-wizard.component';
import { DocPackTypesComponent } from './views/components/doc-pack-types/doc-pack-types.component';
import { DocPackTypeMenuComponent } from './views/components/doc-pack-type-menu/doc-pack-type-menu.component';
import { DocPackTypeComponent } from './views/components/doc-pack-type/doc-pack-type.component';
import { DocPackTypeChapterComponent } from './views/components/doc-pack-type-chapter/doc-pack-type-chapter.component';
import { EmailTemplatesComponent } from './views/components/email-templates/email-templates.component';
import { EmailTemplateComponent } from './views/components/email-template/email-template.component';
import { FabricsGrpsComponent } from './views/components/fabrics-grps/fabrics-grps.component';
import { FabricsGrpComponent } from './views/components/fabrics-grp/fabrics-grp.component';
import { FactoriesComponent } from './views/components/factories/factories.component';
import { FactoryComponent } from './views/components/factory/factory.component';
import { FactoryMenuComponent } from './views/components/factory-menu/factory-menu.component';
import { OperationFactoriesComponent } from './views/components/operation-factories/operation-factories.component';
import { OperationFactoryComponent } from './views/components/operation-factory/operation-factory.component';
import { EquipmentListComponent } from './views/components/equipment-list/equipment-list.component';
import { OeeStateEquipmentsComponent } from './views/components/oee-state-equipments/oee-state-equipments.component';
import { OeeStateEquipmentComponent } from './views/components/oee-state-equipment/oee-state-equipment.component';
import { OeeStateRemarksComponent } from './views/components/oee-state-remarks/oee-state-remarks.component';
import { OeeStateRemarkComponent } from './views/components/oee-state-remark/oee-state-remark.component';
import { ConsignmentPlansConsignmentContentComponent } from './views/components/consignment-plans-consignment-content/consignment-plans-consignment-content.component';
import { DocumentsMergedPreviewComponent } from './views/components/documents/documents-merged-preview/documents-merged-preview.component';
import { WorkGrpsComponent } from './views/components/work-grps/work-grps.component';
import { WorkGrpMenuComponent } from './views/components/work-grp-menu/work-grp-menu.component';
import { WorkGrpComponent } from './views/components/work-grp/work-grp.component';
import { EquipmentGrpsComponent } from './views/components/equipment-grps/equipment-grps.component';
import { EquipmentGrpMenuComponent } from './views/components/equipment-grp-menu/equipment-grp-menu.component';
import { EquipmentGrpLangComponent } from './views/components/equipment-grp-lang/equipment-grp-lang.component';
import { EquipmentGrpComponent } from './views/components/equipment-grp/equipment-grp.component';
import { FactoringsComponent } from './views/components/factorings/factorings.component';
import { ReportPreviewComponent } from './modal/report-preview/report-preview.component';
import { ReportPreviewDialogComponent } from './modal/report-preview-dialog/report-preview-dialog.component';
import { EmailCreateComponent } from './views/components/email-create/email-create.component';
import { FactoringComponent } from './views/components/factoring/factoring.component';
import { ForecastsComponent } from './views/components/forecasts/forecasts.component';
import { ForecastComponent } from './views/components/forecast/forecast.component';
import { FreightRateTypesComponent } from './views/components/freight-rate-types/freight-rate-types.component';
import { FreightRateTypeComponent } from './views/components/freight-rate-type/freight-rate-type.component';
import { FtpAccountsComponent } from './views/components/ftp-accounts/ftp-accounts.component';
import { FtpAccountComponent } from './views/components/ftp-account/ftp-account.component';
import { FurnitureFabricsSComponent } from './views/components/furniture-fabrics-s/furniture-fabrics-s.component';
import { FurnitureFabricsMenuComponent } from './views/components/furniture-fabrics-menu/furniture-fabrics-menu.component';
import { FurnitureFabricsColorsComponent } from './views/components/furniture-fabrics-colors/furniture-fabrics-colors.component';
import { FurnitureFabricsColorComponent } from './views/components/furniture-fabrics-color/furniture-fabrics-color.component';
import { AccountsComponent } from './views/components/accounts/accounts.component';
import { AccountComponent } from './views/components/account/account.component';
import { BusinessSComponent } from './views/components/business-s/business-s.component';
import { BusinessMenuComponent } from './views/components/business-menu/business-menu.component';
import { BusinessLangComponent } from './views/components/business-lang/business-lang.component';
import { BusinessComponent } from './views/components/business/business.component';
import { InterbranchMultiComponent } from './views/components/interbranch-multi/interbranch-multi.component';
import { InterbranchMultiGetComponent } from './views/components/interbranch-multi-get/interbranch-multi-get.component';
import { InterbranchLevelComponent } from './views/components/interbranch-level/interbranch-level.component';
import { InvoiceMethodsComponent } from './views/components/invoice-methods/invoice-methods.component';
import { InvoiceMethodComponent } from './views/components/invoice-method/invoice-method.component';
import { InvoiceMethodLangsComponent } from './views/components/invoice-method-langs/invoice-method-langs.component';
import { RadioButtonGroupComponent } from './core/components/radio-button-group/radio-button-group.component';
import { RadioButtonComponent } from './core/components/radio-button-group/radio-button/radio-button.component';
import { ProductsConfigComponent } from './views/components/products-config/products-config.component';
import { ProductsConfigViewComponent } from './views/components/products-config-view/products-config-view.component';
import { MyPageCustomWidgetComponent } from './views/components/my-page/my-page-custom-widget/my-page-custom-widget.component';
import { KpiCompsComponent } from './views/components/kpi-comps/kpi-comps.component';
import { LanguagesComponent } from './views/components/languages/languages.component';
import { LanguageLangsComponent } from './views/components/language-langs/language-langs.component';
import { LanguageComponent } from './views/components/language/language.component';
import { CarrierListComponent } from './views/components/carrier-list/carrier-list.component';
import { ModelsComponent } from './views/components/models/models.component';
import { ModelLangsComponent } from './views/components/model-langs/model-langs.component';
import { ModelMenuComponent } from './views/components/model-menu/model-menu.component';
import { ModelComponent } from './views/components/model/model.component';
import { OperationMenuComponent } from './views/components/operation-menu/operation-menu.component';
import { OperationLangsComponent } from './views/components/operation-langs/operation-langs.component';
import { OperationComponent } from './views/components/operation/operation.component';
import { OrderLineCancelCausesComponent } from './views/components/order-line-cancel-causes/order-line-cancel-causes.component';
import { OrderLineCancelCauseComponent } from './views/components/order-line-cancel-cause/order-line-cancel-cause.component';
import { OrderLineCancelCauseLangComponent } from './views/components/order-line-cancel-cause-lang/order-line-cancel-cause-lang.component';
import { PersFactsComponent } from './views/components/pers-facts/pers-facts.component';
import { PersFactsEditComponent } from './views/components/pers-facts-edit/pers-facts-edit.component';
import { MetaTableViewsComponent } from './views/components/meta-table-views/meta-table-views.component';
import { MetaTableViewComponent } from './views/components/meta-table-view/meta-table-view.component';
import { MetaTableMenuComponent } from './views/components/meta-table-menu/meta-table-menu.component';
import { MetaTableComponent } from './views/components/meta-table/meta-table.component';
import { MetaColumnsComponent } from './views/components/meta-columns/meta-columns.component';
import { MetaColumnComponent } from './views/components/meta-column/meta-column.component';
import { MetaTablesComponent } from './views/components/meta-tables/meta-tables.component';
import { ProdBeoMethodsComponent } from './views/components/prod-beo-methods/prod-beo-methods.component';
import { PriceIndexesComponent } from './views/components/price-indexes/price-indexes.component';
import { PriceIndexComponent } from './views/components/price-index/price-index.component';
import { PriceIndexMenuComponent } from './views/components/price-index-menu/price-index-menu.component';
import { ProductSettingsComponent } from './views/components/product-settings/product-settings.component';
import { ProductSettingMcComponent } from './views/components/product-setting-mc/product-setting-mc.component';
import { ProdGrpsComponent } from './views/components/prod-grps/prod-grps.component';
import { ProdGrpComponent } from './views/components/prod-grp/prod-grp.component';
import { PurchHeadComponent } from './views/components/purch-head/purch-head.component';
import { PurchHeadHeadComponent } from './views/components/purch-head/purch-head-head/purch-head-head.component';
import { DocumentsGridComponent } from './shared/documents-grid/documents-grid.component';
import { SettingsToolPanelComponent } from './core/components/grid/tool-panels/settings-tool-panel/settings-tool-panel.component';
import { ProdGrpLangsComponent } from './views/components/prod-grp-langs/prod-grp-langs.component';
import { ProdGrp2Component } from './views/components/prod-grp2/prod-grp2.component';
import { ProdGrp2MenuComponent } from './views/components/prod-grp2-menu/prod-grp2-menu.component';
import { ProdGrp3MenuComponent } from './views/components/prod-grp3-menu/prod-grp3-menu.component';
import { ProdGrp4MenuComponent } from './views/components/prod-grp4-menu/prod-grp4-menu.component';
import { ProdGrp4Component } from './views/components/prod-grp4/prod-grp4.component';
import { PurchHeadPurchLinesComponent } from './views/components/purch-head/purch-head-purch-lines/purch-head-purch-lines.component';
import { PurchHeadFunctionsComponent } from './views/components/purch-head/purch-head-functions/purch-head-functions.component';
import { PurchHeadViewComponent } from './views/components/purch-head/purch-head-view/purch-head-view.component';
import { EmailGridComponent } from './shared/email-grid/email-grid.component';
import { TextileModelCategory6sComponent } from './views/components/textile-model-category6s/textile-model-category6s.component';
import { TextileModelCategory6Component } from './views/components/textile-model-category6/textile-model-category6.component';
import { TextileModelCategory6LangComponent } from './views/components/textile-model-category6-lang/textile-model-category6-lang.component';
import { ProductCategorysComponent } from './views/components/product-categorys/product-categorys.component';
import { ProductCategoryComponent } from './views/components/product-category/product-category.component';
import { ProductCategoryLangComponent } from './views/components/product-category-lang/product-category-lang.component';
import { ProductCompositionsComponent } from './views/components/product-compositions/product-compositions.component';
import { ProductCompositionComponent } from './views/components/product-composition/product-composition.component';
import { ProductCompositionLangComponent } from './views/components/product-composition-lang/product-composition-lang.component';
import { TextileModelCategory5sComponent } from './views/components/textile-model-category5s/textile-model-category5s.component';
import { TextileModelCategory5Component } from './views/components/textile-model-category5/textile-model-category5.component';
import { TextileModelCategory5LangComponent } from './views/components/textile-model-category5-lang/textile-model-category5-lang.component';
import { TextileModelCategory4sComponent } from './views/components/textile-model-category4s/textile-model-category4s.component';
import { TextileModelCategory4Component } from './views/components/textile-model-category4/textile-model-category4.component';
import { TextileModelCategory4LangComponent } from './views/components/textile-model-category4-lang/textile-model-category4-lang.component';
import { TextileModelCategory7sComponent } from './views/components/textile-model-category7s/textile-model-category7s.component';
import { TextileModelCategory7Component } from './views/components/textile-model-category7/textile-model-category7.component';
import { TextileModelCategory7LangComponent } from './views/components/textile-model-category7-lang/textile-model-category7-lang.component';
import { TextileModelCategory8sComponent } from './views/components/textile-model-category8s/textile-model-category8s.component';
import { TextileModelCategory8Component } from './views/components/textile-model-category8/textile-model-category8.component';
import { TextileModelCategory8LangComponent } from './views/components/textile-model-category8-lang/textile-model-category8-lang.component';
import { ProdBeoMethodLangsComponent } from './views/components/prod-beo-method-langs/prod-beo-method-langs.component';
import { PurchHeadsComponent } from './views/components/purch-heads/purch-heads.component';
import { ProductionRoutingsComponent } from './views/components/production-routings/production-routings.component';
import { ProductionRoutingMenuComponent } from './views/components/production-routing-menu/production-routing-menu.component';
import { ProductionRoutingComponent } from './views/components/production-routing/production-routing.component';
import { OperationsComponent } from './views/components/operations/operations.component';
import { PropertyClassesComponent } from './views/components/property-classes/property-classes.component';
import { PropertyClassLangsComponent } from './views/components/property-class-langs/property-class-langs.component';
import { SaftMainGroupsComponent } from './views/components/saft-main-groups/saft-main-groups.component';
import { SaftMainGroupComponent } from './views/components/saft-main-group/saft-main-group.component';
import { OrderStatusesComponent } from './views/components/order-statuses/order-statuses.component';
import { OrderStatusLangsComponent } from './views/components/order-status-langs/order-status-langs.component';
import { SeasonalitiesComponent } from './views/components/seasonalities/seasonalities.component';
import { SeasonalityComponent } from './views/components/seasonality/seasonality.component';
import { SopsComponent } from './views/components/sops/sops.component';
import { SopMenuComponent } from './views/components/sop-menu/sop-menu.component';
import { SopComponent } from './views/components/sop/sop.component';
import { ServiceObjectTypesComponent } from './views/components/service-object-types/service-object-types.component';
import { ServiceObjectTypeMenuComponent } from './views/components/service-object-type-menu/service-object-type-menu.component';
import { UserGroupsComponent } from './views/components/user-groups/user-groups.component';
import { UserGroupComponent } from './views/components/user-group/user-group.component';
import { UserGroupViewComponent } from './views/components/user-group/user-group-view/user-group-view.component';
import { CustomerEditComponent } from './views/components/customer-edit/customer-edit.component';
import { PostplaceComponent } from './views/components/postplace/postplace.component';
import { PostplaceViewComponent } from './views/components/postplace/postplace-view/postplace-view.component';
import { ServiceObjectTypeComponent } from './views/components/service-object-type/service-object-type.component';
import { ServiceObjectTypeIntsComponent } from './views/components/service-object-type-ints/service-object-type-ints.component';
import { SkillsCertificateTypesComponent } from './views/components/skills-certificate-types/skills-certificate-types.component';
import { InputPhoneComponent } from './core/components/input-phone/input-phone.component';
import { InputEmailComponent } from './core/components/input-email/input-email.component';
import { SelectGlyphiconComponent } from './views/components/select-glyphicon/select-glyphicon.component';
import { P2StatSourceComponent } from './views/components/p2-stat-source/p2-stat-source.component';
import { P2StatSourceEditComponent } from './views/components/p2-stat-source-edit/p2-stat-source-edit.component';
import { StockCorrCodesComponent } from './views/components/stock-corr-codes/stock-corr-codes.component';
import { StockCorrCodeComponent } from './views/components/stock-corr-code/stock-corr-code.component';
import { StockCorrCodeLangsComponent } from './views/components/stock-corr-code-langs/stock-corr-code-langs.component';
import { StockZoneMenuComponent } from './views/components/stock-zone-menu/stock-zone-menu.component';
import { StockZoneComponent } from './views/components/stock-zone/stock-zone.component';
import { StockLocationComponent } from './views/components/stock-location/stock-location.component';
import { P2TerminalsComponent } from './views/components/p2-terminals/p2-terminals.component';
import { P2TerminalComponent } from './views/components/p2-terminal/p2-terminal.component';
import { PaymentsComponent } from './views/components/payments/payments.component';
import { PaymentComponent } from './views/components/payment/payment.component';
import { PaymentLangsComponent } from './views/components/payment-langs/payment-langs.component';
import { CoopsComponent } from './views/components/coops/coops.component';
import { CoopComponent } from './views/components/coop/coop.component';
import { CoopLangsComponent } from './views/components/coop-langs/coop-langs.component';
import { TravExpTypeMenuComponent } from './views/components/trav-exp-type-menu/trav-exp-type-menu.component';
import { TravExpTypesComponent } from './views/components/trav-exp-types/trav-exp-types.component';
import { TravelWageSetsComponent } from './views/components/travel-wage-sets/travel-wage-sets.component';
import { TravelWageSetMenuComponent } from './views/components/travel-wage-set-menu/travel-wage-set-menu.component';
import { TravelWageSetComponent } from './views/components/travel-wage-set/travel-wage-set.component';
import { UnitsComponent } from './views/components/units/units.component';
import { UnitLangComponent } from './views/components/unit-lang/unit-lang.component';
import { AcVatCodesComponent } from './views/components/ac-vat-codes/ac-vat-codes.component';
import { AcVatCodeComponent } from './views/components/ac-vat-code/ac-vat-code.component';
import { AcVatCodeDataComponent } from './views/components/ac-vat-code-data/ac-vat-code-data.component';
import { DomObjectTypesComponent } from './views/components/dom-object-types/dom-object-types.component';
import { DomObjectTypeMenuComponent } from './views/components/dom-object-type-menu/dom-object-type-menu.component';
import { DomObjectTypePropComponent } from './views/components/dom-object-type-prop/dom-object-type-prop.component';
import { DomObjectTypeComponent } from './views/components/dom-object-type/dom-object-type.component';
import { ProjectFoldersComponent } from './views/components/project-folders/project-folders.component';
import { ProjectFoldersViewComponent } from './views/components/project-folders/project-folders-view/project-folders-view.component';
import { ProjectFolderComponent } from './views/components/project-folders/project-folder/project-folder.component';
import { ProjectProcessComponent } from './views/components/project-process/project-process.component';
import { ProjectProcessViewComponent } from './views/components/project-process/project-process-view/project-process-view.component';
import { SelectLineModelComponent } from './views/components/select-line-model/select-line-model.component';
import { SelectLineModelViewComponent } from './views/components/select-line-model/select-line-model-view/select-line-model-view.component';
import { SelectLineProductsComponent } from './views/components/select-line-products/select-line-products.component';
import { SelectLineProductsViewComponent } from './views/components/select-line-products/select-line-products-view/select-line-products-view.component';
import { MultipleProductsConfigDialogComponent } from './modal/multiple-products-config-dialog/multiple-products-config-dialog.component';
import { PostPlacesComponent } from './views/components/post-places/post-places.component';
import { AccountingAllocationsComponent } from './views/components/accounting-allocations/accounting-allocations.component';
import { AccountingAllocationComponent } from './views/components/accounting-allocation/accounting-allocation.component';
import { AccountingAllocationLineComponent } from './views/components/accounting-allocation-line/accounting-allocation-line.component';
import { GuardListComponent } from './views/components/guard-list/guard-list.component';
import { CheckInMenuComponent } from './views/components/check-in-menu/check-in-menu.component';
import { CheckInStatusComponent } from './views/components/check-in-status/check-in-status.component';
import { P2OffDutyDatasComponent } from './views/components/p2-off-duty-datas/p2-off-duty-datas.component';
import { PayCheckBatchesComponent } from './views/components/pay-check-batches/pay-check-batches.component';
import { PowTimeApprovalsComponent } from './views/components/pow-time-approvals/pow-time-approvals.component';
import { SalaryTypesComponent } from './views/components/salary-types/salary-types.component';
import { CustomerEditViewComponent } from './views/components/customer-edit/customer-edit-view/customer-edit-view.component';
import { SalaryTypeLangComponent } from './views/components/salary-type-lang/salary-type-lang.component';
import { LabourSalaryTypesComponent } from './views/components/labour-salary-types/labour-salary-types.component';
import { LabourSalaryTypeComponent } from './views/components/labour-salary-type/labour-salary-type.component';
import { OffDutyCodesComponent } from './views/components/off-duty-codes/off-duty-codes.component';
import { OffDutyCodeComponent } from './views/components/off-duty-code/off-duty-code.component';
import { PowtimesGridComponent } from './shared/powtimes-grid/powtimes-grid.component';
import { FileDataViewerComponent } from './core/components/file-data-viewer/file-data-viewer.component';
import { DoDataImpComponent } from './views/components/do-data-imp/do-data-imp.component';
import { DoDataImpImportDialogComponent } from './views/components/do-data-imp/do-data-imp-import-dialog/do-data-imp-import-dialog.component';
import { DoDataImpViewComponent } from './views/components/do-data-imp/do-data-imp-view/do-data-imp-view.component';
import { HolidaysViewComponent } from './views/components/holidays-view/holidays-view.component';
import { HolidaysComponent } from './views/components/holidays/holidays.component';
import { HolidayComponent } from './views/components/holiday/holiday.component';
import { SubscriptionGridComponent } from './views/components/subscription-grid/subscription-grid.component';
import { ServiceObjectsComponent } from './views/components/service-objects/service-objects.component';
import { ServiceObjectComponent } from './views/components/service-object/service-object.component';
import { FindPersonComponent } from './views/components/find-person/find-person.component';
import { CustomerPersonsComponent } from './views/components/customer-persons/customer-persons.component';
import { CustomerDiscGrpProdComponent } from './views/components/customer-disc-grp-prod/customer-disc-grp-prod.component';
import { CheckListDatasComponent } from './views/components/check-list-datas/check-list-datas.component';
import { SelectCheckListAddComponent } from './views/components/select-check-list-add/select-check-list-add.component';
import { CustFactsComponent } from './views/components/cust-facts/cust-facts.component';
import { ActivityGridComponent } from './views/components/activity-grid/activity-grid.component';
import { CustactDeadLinesComponent } from './views/components/custact-dead-lines/custact-dead-lines.component';
import { RfidSearchComponent } from './views/components/rfid-search/rfid-search.component';
import { IncomingCallComponent } from './views/components/incoming-call/incoming-call.component';
import { VisitorComponent } from './views/components/visitor/visitor.component';
import { WebsitesComponent } from './views/components/websites/websites.component';
import { MetaTableDatasComponent } from './views/components/meta-table-datas/meta-table-datas.component';
import { SelectArticlesComponent } from './views/components/select-articles/select-articles.component';
import { InfoScreenComponent } from './views/components/info-screen/info-screen.component';
import { EmailArchiveDialogComponent } from './modal/email-archive-dialog/email-archive-dialog.component';
import { EmailPersonInfoDialogComponent } from './modal/email-person-info-dialog/email-person-info-dialog.component';
import { EmailPersonInfoFormComponent } from './views/components/email/email-person-info-form/email-person-info-form.component';
import { EmailMoveToFolderComponent } from './modal/email-move-to-folder/email-move-to-folder.component';
import { PurchPlanningComponent } from './views/components/purch-planning/purch-planning.component';
import { StockGetComponent } from './views/components/stock-get/stock-get.component';
import { StockOnLocationComponent } from './views/components/stock-on-location/stock-on-location.component';
import { StockAllComponent } from './views/components/stock-all/stock-all.component';
import { StockIncomingComponent } from './views/components/stock-incoming/stock-incoming.component';
import { LookupMultiCellEditorComponent } from './core/components/grid/cell-editors/lookup-multi-cell-editor/lookup-multi-cell-editor.component';
import { CreatePoPuComponent } from './views/components/create-po-pu/create-po-pu.component';
import { StockHistoryComponent } from './views/components/stock-history/stock-history.component';
import { PohTopComponent } from './views/components/poh-top/poh-top.component';
import { ChipCellRendererComponent } from './core/components/grid/cell-renderers/chip-cell-renderer/chip-cell-renderer.component';
import { OrderlineCalcFormComponent } from './views/components/orderline-calc-form/orderline-calc-form.component';
import { OrderlineCalcFormViewComponent } from './views/components/orderline-calc-form/orderline-calc-form-view/orderline-calc-form-view.component';
import { PohComponent } from './views/components/poh/poh.component';
import { StockOutgoingComponent } from './views/components/stock-outgoing/stock-outgoing.component';
import { ProductPricesComponent } from './views/components/product-prices/product-prices.component';
import { PriceComponent } from './views/components/price/price.component';
import { PriceListFactorsComponent } from './views/components/price-list-factors/price-list-factors.component';
import { ProductionRoutingCompComponent } from './views/components/production-routing-comp/production-routing-comp.component';
import { ProductionRoutingCompViewComponent } from './views/components/production-routing-comp/production-routing-comp-view/production-routing-comp-view.component';
import { ComponentsComponent } from './views/components/components/components.component';
import { ComponentAlternativesComponent } from './views/components/component-alternatives/component-alternatives.component';
import { ComponentAlternativeComponent } from './views/components/component-alternative/component-alternative.component';
import { ProductPicsComponent } from './views/components/product-pics/product-pics.component';
import { ProductPicComponent } from './views/components/product-pic/product-pic.component';
import { ChoicesComponent } from './views/components/choices/choices.component';
import { ChoiceComponent } from './views/components/choice/choice.component';
import { ChoicePossibilityListComponent } from './views/components/choice-possibility-list/choice-possibility-list.component';
import { AcLedgerViewComponent } from './views/components/ac-ledger-view/ac-ledger-view.component';
import { ProdSizesComponent } from './views/components/prod-sizes/prod-sizes.component';
import { ProdSizeEditComponent } from './views/components/prod-size-edit/prod-size-edit.component';
import { ProdSizeColliComponent } from './views/components/prod-size-colli/prod-size-colli.component';
import { ProductLinksComponent } from './views/components/product-links/product-links.component';
import { ProductLinkComponent } from './views/components/product-link/product-link.component';
import { DoDataImpsWizardEditComponent } from './views/components/do-data-imps-wizard-edit/do-data-imps-wizard-edit.component';
import { ChipFloatingFilterComponent } from './core/components/grid/floating-filters/chip-floating-filter/chip-floating-filter.component';
import { ChipFilterComponent } from './core/components/grid/filters/chip-filter/chip-filter.component';
import { GridToolbarComponent } from './core/components/grid/toolbar/grid-toolbar/grid-toolbar.component';
import { GridButtonComponent } from './core/components/grid/toolbar/grid-button/grid-button.component';
import { ProductLinksChangeComponent } from './views/components/product-links-change/product-links-change.component';
import { ProductMultiUnitComponent } from './views/components/product-multi-unit/product-multi-unit.component';
import { ProductUnitsComponent } from './views/components/product-units/product-units.component';
import { ProductUnitComponent } from './views/components/product-unit/product-unit.component';
import { ProductUnitsEditComponent } from './views/components/product-units-edit/product-units-edit.component';
import { ProductUnitDataComponent } from './views/components/product-unit-data/product-unit-data.component';
import { ProductEansComponent } from './views/components/product-eans/product-eans.component';
import { EdgeRelationsComponent } from './views/components/edge-relations/edge-relations.component';
import { EdgeRelationComponent } from './views/components/edge-relation/edge-relation.component';
import { SelectableProductsComponent } from './views/components/selectable-products/selectable-products.component';
import { CalcCopyFromComponent } from './views/components/calc-copy-from/calc-copy-from.component';
import { CalcCopyFromViewComponent } from './views/components/calc-copy-from/calc-copy-from-view/calc-copy-from-view.component';
import { ComponentReplacementsComponent } from './views/components/component-replacements/component-replacements.component';
import { ProductWasteComponent } from './views/components/product-waste/product-waste.component';
import { PriceListsComponent } from './views/components/price-lists/price-lists.component';
import { PriceListComponent } from './views/components/price-list/price-list.component';
import { ProjectProgressLogComponent } from './views/components/project-progress-log/project-progress-log.component';
import { ProjectProgressLogViewComponent } from './views/components/project-progress-log/project-progress-log-view/project-progress-log-view.component';
import { MaterialGradesComponent } from './views/components/material-grades/material-grades.component';
import { MaterialGradesViewComponent } from './views/components/material-grades/material-grades-view/material-grades-view.component';
import { CertificateRequirementsComponent } from './views/components/certificate-requirements/certificate-requirements.component';
import { CertificateRequirementsViewComponent } from './views/components/certificate-requirements/certificate-requirements-view/certificate-requirements-view.component';
import { UltraSonicClassesComponent } from './views/components/ultra-sonic-classes/ultra-sonic-classes.component';
import { UltraSonicClassesViewComponent } from './views/components/ultra-sonic-classes/ultra-sonic-classes-view/ultra-sonic-classes-view.component';
import { MaterialGradeComponent } from './views/components/material-grade/material-grade.component';
import { MaterialGradeViewComponent } from './views/components/material-grade/material-grade-view/material-grade-view.component';
import { PriceListBusCosComponent } from './views/components/price-list-bus-cos/price-list-bus-cos.component';
import { PriceListBusCoComponent } from './views/components/price-list-bus-co/price-list-bus-co.component';
import { ChecklistDataComponent } from './views/components/checklist-data/checklist-data.component';
import { ChecklistDataViewComponent } from './views/components/checklist-data/checklist-data-view/checklist-data-view.component';
import { InputSignatureComponent } from './core/components/input-signature/input-signature.component';
import { CollectionsComponent } from './views/components/collections/collections.component';
import { CollectionMenuComponent } from './views/components/collection-menu/collection-menu.component';
import { CollectionLangsComponent } from './views/components/collection-langs/collection-langs.component';
import { CollectionComponent } from './views/components/collection/collection.component';
import { ProductLangsComponent } from './views/components/product-langs/product-langs.component';
import { ProductLangComponent } from './views/components/product-lang/product-lang.component';
import { PrepareBomsComponent } from './views/components/prepare-boms/prepare-boms.component';
import { TextileStockSearchComponent } from './views/components/textile-stock-search/textile-stock-search.component';
import { StockBatchSearchComponent } from './views/components/stock-batch-search/stock-batch-search.component';
import { StockBatchCorrQtyComponent } from './views/components/stock-batch-corr-qty/stock-batch-corr-qty.component';
import { StockCorrectionsComponent } from './views/components/stock-corrections/stock-corrections.component';
import { StockHistoricComponent } from './views/components/stock-historic/stock-historic.component';
import { DefaultCountersComponent } from './views/components/default-counters/default-counters.component';
import { StockControlSpListComponent } from './views/components/stock-control-sp-list/stock-control-sp-list.component';
import { BusinessCoDefaultCountersComponent } from './views/components/business-co-default-counters/business-co-default-counters.component';
import { BusinessCoDefaultCounterComponent } from './views/components/business-co-default-counter/business-co-default-counter.component';
import { StepperComponent } from './core/components/stepper/stepper.component';
import { StepComponent } from './core/components/stepper/step/step.component';
import { CalViewComponent } from './views/components/cal-view/cal-view.component';
import { CalViewViewComponent } from './views/components/cal-view/cal-view-view/cal-view-view.component';
import { StockCountsComponent } from './views/components/stock-counts/stock-counts.component';
import { StockCountMenuComponent } from './views/components/stock-count-menu/stock-count-menu.component';
import { StockCountDataComponent } from './views/components/stock-count-data/stock-count-data.component';
import { StockCountDatasComponent } from './views/components/stock-count-datas/stock-count-datas.component';
import { ChargesComponent } from './views/components/charges/charges.component';
import { StockCountRefreshComponent } from './views/components/stock-count-refresh/stock-count-refresh.component';
import { StockCountComponent } from './views/components/stock-count/stock-count.component';
import { ChargeComponent } from './views/components/charge/charge.component';
import { ChargeViewComponent } from './views/components/charge/charge-view/charge-view.component';
import { ChargeTestComponent } from './views/components/charge-test/charge-test.component';
import { ChargeTestViewComponent } from './views/components/charge-test/charge-test-view/charge-test-view.component';
import { StockCountResultComponent } from './views/components/stock-count-result/stock-count-result.component';
import { StockTransfersComponent } from './views/components/stock-transfers/stock-transfers.component';
import { StockTransferCreateComponent } from './views/components/stock-transfer-create/stock-transfer-create.component';
import { RequisitionsComponent } from './views/components/requisitions/requisitions.component';
import { RequisitionComponent } from './views/components/requisition/requisition.component';
import { OrderPickNewComponent } from './views/components/order-pick-new/order-pick-new.component';
import { OrderPickNewViewComponent } from './views/components/order-pick-new/order-pick-new-view/order-pick-new-view.component';
import { OrderPickViewComponent } from './views/components/order-pick-view/order-pick-view.component';
import { OrderPickViewViewComponent } from './views/components/order-pick-view/order-pick-view-view/order-pick-view-view.component';
import { CuttingPlansComponent } from './views/components/cutting-plans/cutting-plans.component';
import { PoLoadPlanComponent } from './views/components/po-load-plan/po-load-plan.component';
import { PoComponent } from './views/components/po/po.component';
import { OhMaterialCertificatesComponent } from './views/components/oh-material-certificates/oh-material-certificates.component';
import { MatcertCreateComponent } from './views/components/oh-material-certificates/matcert-create/matcert-create.component';
import { MatcertComponent } from './views/components/oh-material-certificates/matcert/matcert.component';
import { GridToggleComponent } from './core/components/grid/toolbar/grid-toggle/grid-toggle.component';
import { GridToggleStateComponent } from './core/components/grid/toolbar/grid-toggle/grid-toggle-state/grid-toggle-state.component';
import { ShopFloorSelectOhComponent } from './views/components/shop-floor-select-oh/shop-floor-select-oh.component';
import { DynamicDatataskGotoComponent } from './views/components/dynamic-datatask-goto/dynamic-datatask-goto.component';
import { EmailFolderTreeDataSourceDirective } from './views/components/email/email-folder-tree-data-source.directive';
import { EmailMailFoldersComponent } from './views/components/email/email-mail-folders/email-mail-folders.component';
import { RequisitionPlanViewComponent } from './views/components/requisition-plan-view/requisition-plan-view.component';
import { RequisitionCreateComponent } from './views/components/requisition-create/requisition-create.component';
import { StockTransferLogComponent } from './views/components/stock-transfer-log/stock-transfer-log.component';
import { OeeStatesComponent } from './views/components/oee-states/oee-states.component';
import { OeeStateEditComponent } from './views/components/oee-state-edit/oee-state-edit.component';
import { EquipmentsComponent } from './views/components/equipments/equipments.component';
import { QuickAccessComponent } from './views/components/quick-access/quick-access.component';
import { AppBreadcrumbsComponent } from './core/components/app-breadcrumbs/app-breadcrumbs.component';
import { BreadcrumbsComponent } from './core/components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbItemComponent } from './core/components/breadcrumbs/breadcrumb-item/breadcrumb-item.component';
import { LabourPositionsComponent } from './views/components/labour-positions/labour-positions.component';
import { MaterialItemsComponent } from './views/components/material-items/material-items.component';
import { OperationEquipmentsComponent } from './views/components/operation-equipments/operation-equipments.component';
import { PaperlessProductionComponent } from './views/components/paperless-production/paperless-production.component';
import { PaperlessPoComponent } from './views/components/paperless-po/paperless-po.component';
import { OrderLineCreditsComponent } from './views/components/order-line-credits/order-line-credits.component';
import { MaterialItemComponent } from './views/components/material-item/material-item.component';
import { MaterialItemViewComponent } from './views/components/material-item/material-item-view/material-item-view.component';
import { StockBatchLoggComponent } from './views/components/stock-batch-logg/stock-batch-logg.component';
import { DocPackTypeFormatComponent } from './views/components/doc-pack-type-menu/doc-pack-type-format/doc-pack-type-format.component';
import { DocPackTypeViewComponent } from './views/components/doc-pack-type/doc-pack-type-view/doc-pack-type-view.component';
import { OrderLineCreditComponent } from './views/components/order-line-credit/order-line-credit.component';
import { ConsignmentShipmentsComponent } from './views/components/consignment-shipments/consignment-shipments.component';
import { ConsignmentShipmentComponent } from './views/components/consignment-shipment/consignment-shipment.component';
import { PickJobGrpBatchesPickerComponent } from './views/components/pick-job-grp-batches-picker/pick-job-grp-batches-picker.component';
import { PickJobGrpBatchesComponent } from './views/components/pick-job-grp-batches/pick-job-grp-batches.component';
import { SubtasksComponent } from './views/components/subtasks/subtasks.component';
import { MaterialItemPickComponent } from './views/components/material-item-pick/material-item-pick.component';
import { DocPackTypeChaptersComponent } from './views/components/doc-pack-type-menu/doc-pack-type-chapters/doc-pack-type-chapters.component';
import { DocPackComponent } from './views/components/doc-pack/doc-pack.component';
import { DocPackChapterComponent } from './views/components/doc-pack/doc-pack-chapter/doc-pack-chapter.component';
// import { ConsignmentBatchConsignmentsComponent } from './views/components/consignment-batch-consignments/consignment-batch-consignments.component';
import { RequisitionsBetweenComponent } from './views/components/requisitions-between/requisitions-between.component';
import { TravelBillsComponent } from './views/components/travel-bills/travel-bills.component';
import { TravelWageDataComponent } from './views/components/travel-wage-data/travel-wage-data.component';
import { TravelBillMenuComponent } from './views/components/travel-bill-menu/travel-bill-menu.component';
import { OrderHeadComponent } from './views/components/order-head/order-head.component';
import { OrderLinesGridComponent } from './views/components/order-head/order-lines-grid/order-lines-grid.component';
import { OrderHeadCancelOrderlinesDialogComponent } from './modal/order-head-cancel-orderlines-dialog/order-head-cancel-orderlines-dialog.component';
import { AcCustTransesAgeComponent } from './views/components/ac-cust-transes-age/ac-cust-transes-age.component';
import { VatReportsComponent } from './views/components/vat-reports/vat-reports.component';
import { VatReportComponent } from './views/components/vat-report/vat-report.component';
import { PagerComponent } from './core/components/pager/pager.component';
import { VatSpesificationComponent } from './views/components/vat-spesification/vat-spesification.component';
import { ReceivablesComponent } from './views/components/receivables/receivables.component';
import { BankReconciliationsComponent } from './views/components/bank-reconciliations/bank-reconciliations.component';
import { CollectionCalculationComponent } from './views/components/collection-calculation/collection-calculation.component';
import { InterbranchInvoicingComponent } from './views/components/interbranch-invoicing/interbranch-invoicing.component';
import { InterbranchInvoicingMiComponent } from './views/components/interbranch-invoicing-mi/interbranch-invoicing-mi.component';
import { CustomerInvoicingsComponent } from './views/components/customer-invoicings/customer-invoicings.component';
import { CustomerInvoicingComponent } from './views/components/customer-invoicing/customer-invoicing.component';
import { DataMiningListComponent } from './views/components/data-mining-list/data-mining-list.component';
import { PurchHeadCreateComponent } from './views/components/purch-head-create/purch-head-create.component';
import { SelectProjectComponent } from './views/components/select-project/select-project.component';
import { SelectProjectViewComponent } from './views/components/select-project/select-project-view/select-project-view.component';
import { DataMiningTableComponent } from './views/components/data-mining-table/data-mining-table.component';
import { AccountingBuntsComponent } from './views/components/accounting-bunts/accounting-bunts.component';
import { FactoringBuntsComponent } from './views/components/factoring-bunts/factoring-bunts.component';
import { FactoringBuntComponent } from './views/components/factoring-bunt/factoring-bunt.component';
import { ProjectInvoicingsComponent } from './views/components/project-invoicings/project-invoicings.component';
import { PurchHeadReceiveSimplifiedComponent } from './modal/purch-head-receive-simplified/purch-head-receive-simplified.component';
import { ShopFloorComponent } from './views/components/shop-floor/shop-floor.component';
import { ProjectInvoicingComponent } from './views/components/project-invoicing/project-invoicing.component';
import { TimeInOutsComponent } from './views/components/time-in-outs/time-in-outs.component';
import { PowTimePayCheckLsComponent } from './views/components/pow-time-pay-check-ls/pow-time-pay-check-ls.component';
import { DeviationsComponent } from './views/components/deviations/deviations.component';
import { DeviationComponent } from './views/components/deviation/deviation.component';
import { CustactMembersComponent } from './views/components/custact-members/custact-members.component';
import { CustactCustomersComponent } from './views/components/custact-customers/custact-customers.component';
import { CustactCustomerComponent } from './views/components/custact-customer/custact-customer.component';
import { BiReportsComponent } from './views/components/bi-reports/bi-reports.component';
import { AcSuppTransesAgeComponent } from './views/components/ac-supp-transes-age/ac-supp-transes-age.component';
import { AcSuppTransComponent } from './views/components/ac-supp-trans/ac-supp-trans.component';
import { DoDataImpsComponent } from './views/components/do-data-imps/do-data-imps.component';
import { CustGrp2sComponent } from './views/components/cust-grp2s/cust-grp2s.component';
import { CustGrp2Component } from './views/components/cust-grp2/cust-grp2.component';
import { CustGrp2ViewComponent } from './views/components/cust-grp2/cust-grp2-view/cust-grp2-view.component';
import { DataImpsComponent } from './views/components/data-imps/data-imps.component';
import { DataImpComponent } from './views/components/data-imp/data-imp.component';
import { GridExportsComponent } from './views/components/grid-exports/grid-exports.component';
import { P2ExportProcsComponent } from './views/components/p2-export-procs/p2-export-procs.component';
import { P2ExportProcMenuComponent } from './views/components/p2-export-proc-menu/p2-export-proc-menu.component';
import { P2ExportProcUserGroupsComponent } from './views/components/p2-export-proc-user-groups/p2-export-proc-user-groups.component';
import { P2ImportProcsComponent } from './views/components/p2-import-procs/p2-import-procs.component';
import { P2ImportProcComponent } from './views/components/p2-import-proc/p2-import-proc.component';
import { JobSchedulesComponent } from './views/components/job-schedules/job-schedules.component';
import { JobQueueMonitorComponent } from './views/components/job-queue-monitor/job-queue-monitor.component';
import { DocArticleSearchComponent } from './views/components/doc-article-search/doc-article-search.component';
import { EdiImportsComponent } from './views/components/edi-imports/edi-imports.component';
import { EdiExportsComponent } from './views/components/edi-exports/edi-exports.component';
import { EdiSourcesComponent } from './views/components/edi-sources/edi-sources.component';
import { EdiDestinationsComponent } from './views/components/edi-destinations/edi-destinations.component';
import { TimeSheetsComponent } from './views/components/time-sheets/time-sheets.component';
import { BspsComponent } from './views/components/bsps/bsps.component';
import { MieTablesComponent } from './views/components/mie-tables/mie-tables.component';
import { MsgCreateComponent } from './views/components/msg-create/msg-create.component';
import { MenuGrpsComponent } from './views/components/menu-grps/menu-grps.component';
import { BatchDeliveriesComponent } from './views/components/batch-deliveries/batch-deliveries.component';
import { BatchDelPurchLineComponent } from './views/components/batch-del-purch-line/batch-del-purch-line.component';
import { BatchDelPurchLinesBsComponent } from './views/components/batch-del-purch-lines-bs/batch-del-purch-lines-bs.component';
import { BatchDeliveryAddComponent } from './views/components/batch-delivery-add/batch-delivery-add.component';
import { BatchDeliveryListComponent } from './views/components/batch-delivery-list/batch-delivery-list.component';
import { BatchDelReceiveLinesComponent } from './views/components/batch-del-receive-lines/batch-del-receive-lines.component';
import { BatchDelControlComponent } from './views/components/batch-del-control/batch-del-control.component';
import { BdScanDetailComponent } from './views/components/bd-scan-detail/bd-scan-detail.component';
import { BatchDelReceiveLineComponent } from './views/components/batch-del-receive-line/batch-del-receive-line.component';
import { BusinessCosComponent } from './views/components/business-cos/business-cos.component';
import { CheckListsComponent } from './views/components/check-lists/check-lists.component';
import { CheckPointsComponent } from './views/components/check-points/check-points.component';
import { CheckPointHeadsComponent } from './views/components/check-point-heads/check-point-heads.component';
import { ColorGrpsComponent } from './views/components/color-grps/color-grps.component';
import { ColorGrpMenuComponent } from './views/components/color-grp-menu/color-grp-menu.component';
import { ColorGrpLangComponent } from './views/components/color-grp-lang/color-grp-lang.component';
import { ColorGrpComponent } from './views/components/color-grp/color-grp.component';
import { ConsignmentLineComponent } from './views/components/consignment-line/consignment-line.component';
import { ConsignmentLinesBsComponent } from './views/components/consignment-lines-bs/consignment-lines-bs.component';
import { ConsignmentLinesBComponent } from './views/components/consignment-lines-b/consignment-lines-b.component';
import { ConsignmentLinesComponent } from './views/components/consignment-lines/consignment-lines.component';
import { ConsignmentOlComponent } from './views/components/consignment-ol/consignment-ol.component';
import { CustactWorksComponent } from './views/components/custact-works/custact-works.component';
import { CustactMemberEditComponent } from './views/components/custact-member-edit/custact-member-edit.component';
import { CustomerBusCosComponent } from './views/components/customer-bus-cos/customer-bus-cos.component';
import { CustomerBusCoComponent } from './views/components/customer-bus-co/customer-bus-co.component';
import { CuttingPlanPoAddComponent } from './views/components/cutting-plan-po-add/cutting-plan-po-add.component';
import { DataMiningCreateComponent } from './views/components/data-mining-create/data-mining-create.component';
import { DeliveryCustomersComponent } from './views/components/delivery-customers/delivery-customers.component';
import { DeliveryCustomerComponent } from './views/components/delivery-customer/delivery-customer.component';
import { CuttingPlanComponent } from './views/components/cutting-plan/cutting-plan.component';
import { OrderHeadsComponent } from './views/components/order-heads/order-heads.component';
import { OrderHeadsFilterComponent } from './views/components/order-heads/order-heads-filter/order-heads-filter.component';
import { OrderHeadsPagedComponent } from './views/components/order-heads/order-heads-paged/order-heads-paged.component';
import { PaginatorIntlService } from './core/models/paginator-intl.service';
import { CustactEditComponent } from './views/components/custact-edit/custact-edit.component';
import { CuttingPlanRealComponent } from './views/components/cutting-plan-real/cutting-plan-real.component';
import { DeviationTypeMenuComponent } from './views/components/deviation-type-menu/deviation-type-menu.component';
import { DeviationTypeLangsComponent } from './views/components/deviation-type-langs/deviation-type-langs.component';
import { EdiDestinationComponent } from './views/components/edi-destination/edi-destination.component';
import { EdiSourceComponent } from './views/components/edi-source/edi-source.component';
import { ExternalApisComponent } from './views/components/external-apis/external-apis.component';
import { ExternalApiComponent } from './views/components/external-api/external-api.component';
import { FreightRateSetsComponent } from './views/components/freight-rate-sets/freight-rate-sets.component';
import { FreightRateComponent } from './views/components/freight-rate/freight-rate.component';
import { InvoiceMakeMultiOrdersComponent } from './views/components/invoice-make-multi-orders/invoice-make-multi-orders.component';
import { InvoiceMakeOverviewComponent } from './views/components/invoice-make-overview/invoice-make-overview.component';
import { LabourJobNoteComponent } from './views/components/labour-job-note/labour-job-note.component';
import { OhDirectDeliveryComponent } from './views/components/oh-direct-delivery/oh-direct-delivery.component';
import { OhDispatchesComponent } from './views/components/oh-dispatches/oh-dispatches.component';
import { OrderHeadCalcsComponent } from './views/components/order-head-calcs/order-head-calcs.component';
import { OrderDeliveryInfoComponent } from './views/components/order-delivery-info/order-delivery-info.component';
import { OrderHeadSetDiscComponent } from './views/components/order-head-set-disc/order-head-set-disc.component';
import { OrderLineMsComponent } from './views/components/order-line-ms/order-line-ms.component';
import { OrderLineStatusComponent } from './views/components/order-line-status/order-line-status.component';
import { OrderPurchMenuComponent } from './views/components/order-purch-menu/order-purch-menu.component';
import { PoMenuComponent } from './views/components/po-menu/po-menu.component';
import { PortalUserSignatureComponent } from './views/components/portal-user-signature/portal-user-signature.component';
import { PositionsComponent } from './views/components/positions/positions.component';
import { PositionComponent } from './views/components/position/position.component';
import { PrepaymentsComponent } from './views/components/prepayments/prepayments.component';
import { PrepaymentComponent } from './views/components/prepayment/prepayment.component';
import { PrepickCreateComponent } from './views/components/prepick-create/prepick-create.component';
import { SelectShiftComponent } from './views/components/select-shift/select-shift.component';
import { SelectOperationPuComponent } from './views/components/select-operation-pu/select-operation-pu.component';
import { SelectPortalUserComponent } from './views/components/select-portal-user/select-portal-user.component';
import { SelectEquipmentPuComponent } from './views/components/select-equipment-pu/select-equipment-pu.component';
import { WorkDetailsComponent } from './views/components/work-details/work-details.component';
import { WorkDetailsRegisterFunctionsComponent } from './views/components/work-details/work-details-register-functions/work-details-register-functions.component';
import { WorkDetailsPlanFunctionsComponent } from './views/components/work-details/work-details-plan-functions/work-details-plan-functions.component';
import { WorkDetailsDesktopComponent } from './views/components/work-details/work-details-desktop/work-details-desktop.component';
import { WorkDetailsMobileComponent } from './views/components/work-details/work-details-mobile/work-details-mobile.component';
import { WorkDetailsPlansFunctionsComponent } from './views/components/work-details/work-details-plans-functions/work-details-plans-functions.component';
import { RelatedPurchOrdersComponent } from './views/components/related-purch-orders/related-purch-orders.component';
import { RequisitionCreateFromComponent } from './views/components/requisition-create-from/requisition-create-from.component';
import { ProjectProcessesComponent } from './views/components/project-processes/project-processes.component';
import { ProductViewComponent } from './views/components/product-view/product-view.component';
import { ServiceObjectNewActComponent } from './views/components/service-object-new-act/service-object-new-act.component';
import { ShipmentContainersComponent } from './views/components/shipment-containers/shipment-containers.component';
import { ShipmentContainerComponent } from './views/components/shipment-container/shipment-container.component';
import { SopClsComponent } from './views/components/sop-cls/sop-cls.component';
import { AccountingProcessingCarouselComponent } from './views/components/accounting-processing/accounting-processing-carousel/accounting-processing-carousel.component';
import { AccountingProcessingListComponent } from './views/components/accounting-processing/accounting-processing-list/accounting-processing-list.component';
import { SopClComponent } from './views/components/sop-cl/sop-cl.component';
import { StockBatchListComponent } from './views/components/stock-batch-list/stock-batch-list.component';
import { StockStockBatchAssignedComponent } from './views/components/stock-stock-batch-assigned/stock-stock-batch-assigned.component';
import { StockTransAllocationsComponent } from './views/components/stock-trans-allocations/stock-trans-allocations.component';
import { CustomersMenuComponent } from './views/components/customers-menu/customers-menu.component';
import { ProjectsGridComponent } from './shared/projects-grid/projects-grid.component';
import { ActivitiesGridComponent } from './shared/activities-grid/activities-grid.component';
import { OrderHeadsGridComponent } from './shared/order-heads-grid/order-heads-grid.component';
import { CustomersMenuFunctionsComponent } from './views/components/customers-menu/customers-menu-functions/customers-menu-functions.component';
import { StockTransMenuComponent } from './views/components/stock-trans-menu/stock-trans-menu.component';
import { StockVariantsComponent } from './views/components/stock-variants/stock-variants.component';
import { SubscriptionsComponent } from './views/components/subscriptions/subscriptions.component';
import { TimeCustomersComponent } from './views/components/time-customers/time-customers.component';
import { TimeCustomersWoComponent } from './views/components/time-customers-wo/time-customers-wo.component';
import { PersonComponent } from './views/components/person/person.component';
import { CustomerClaimsComponent } from './views/components/customer-claims/customer-claims.component';
import { GanttComponent } from './core/components/gantt/gantt.component';
import { GanttToolbarComponent } from './core/components/gantt/gantt-toolbar/gantt-toolbar.component';
import { TimeProjectsComponent } from './views/components/time-projects/time-projects.component';
import { TimeSheetPowTimeListComponent } from './views/components/time-sheet-pow-time-list/time-sheet-pow-time-list.component';
import { TravelWagesComponent } from './views/components/travel-wages/travel-wages.component';
import { ValutasComponent } from './views/components/valutas/valutas.component';
import { VisitorConfirmComponent } from './views/components/visitor-confirm/visitor-confirm.component';
import { AccountingAllocationBasisComponent } from './views/components/accounting-allocation-basis/accounting-allocation-basis.component';
import { UnitComponent } from './views/components/unit/unit.component';
import { TravExpTypeComponent } from './views/components/trav-exp-type/trav-exp-type.component';
import { OrderHeadMiComponent } from './views/components/order-head-mi/order-head-mi.component';
import { WebPageSubscriptionComponent } from './views/components/web-page-subscription/web-page-subscription.component';
import { WebPageLinkComponent } from './views/components/web-page-link/web-page-link.component';
import { TrMenuComponent } from './views/components/tr-menu/tr-menu.component';
import { P2WebpageTableComponent } from './views/components/p2-webpage-table/p2-webpage-table.component';
import { WebpageTableEditComponent } from './views/components/webpage-table-edit/webpage-table-edit.component';
import { TrNewJobSelectOpComponent } from './views/components/tr-new-job-select-op/tr-new-job-select-op.component';
import { TravelBillRejectComponent } from './views/components/travel-bill-reject/travel-bill-reject.component';
import { SopValidComponent } from './views/components/sop-valid/sop-valid.component';
import { SelectTextileModelComponent } from './views/components/select-textile-model/select-textile-model.component';
import { SelectStockLocationComponent } from './views/components/select-stock-location/select-stock-location.component';
import { MultiDocComponent } from './views/components/multi-doc/multi-doc.component';
import { DeviationTypesComponent } from './views/components/deviation-types/deviation-types.component';
import { CustactCalcAdjustDiscComponent } from './views/components/custact-calc-adjust-disc/custact-calc-adjust-disc.component';
import { CuttingplansChangeComponentComponent } from './views/components/cuttingplans-change-component/cuttingplans-change-component.component';
import { CustactCalcPickSelectComponent } from './views/components/custact-calc-pick-select/custact-calc-pick-select.component';
import { CheckListItemItemsComponent } from './views/components/check-list-item-items/check-list-item-items.component';
import { CheckListItemItemComponent } from './views/components/check-list-item-item/check-list-item-item.component';
import { CargonizerPrintersComponent } from './views/components/cargonizer-printers/cargonizer-printers.component';
import { CargonizerPrinterEditComponent } from './views/components/cargonizer-printer-edit/cargonizer-printer-edit.component';
import { ConsignmentLineSelectComponent } from './views/components/consignment-line-select/consignment-line-select.component';
import { ConsignmentSplitLineComponent } from './views/components/consignment-split-line/consignment-split-line.component';
import { CustGrpComponent } from './views/components/cust-grp/cust-grp.component';
import { FreightRateSetCreateComponent } from './views/components/freight-rate-set-create/freight-rate-set-create.component';
import { FinishPohScanComponent } from './views/components/finish-poh-scan/finish-poh-scan.component';
import { ActivityTypeComponent } from './views/components/activity-type/activity-type.component';
import { ActStatusComponent } from './views/components/act-status/act-status.component';
import { BusinessCoSettingCargonizerComponent } from './views/components/business-co-setting-cargonizer/business-co-setting-cargonizer.component';
import { CategoryLinkAddComponent } from './views/components/category-link-add/category-link-add.component';
import { TrNewJobConfComponent } from './views/components/tr-new-job-conf/tr-new-job-conf.component';
import { OrderDialogComponent } from './views/components/order-dialog/order-dialog.component';
import { OrderHeadCopyComponent } from './views/components/order-head-copy/order-head-copy.component';
import { CheckInComponent } from './views/components/check-in/check-in.component';
import { ChoiceProdPriceGrpsComponent } from './views/components/choice-prod-price-grps/choice-prod-price-grps.component';
import { ChoiceProdPriceGrpComponent } from './views/components/choice-prod-price-grp/choice-prod-price-grp.component';
import { ConsignmentColliDetailComponent } from './views/components/consignment-colli-detail/consignment-colli-detail.component';
import { FurnitureFabricsComponent } from './views/components/furniture-fabrics/furniture-fabrics.component';
import { OeeStateLogComponent } from './views/components/oee-state-log/oee-state-log.component';
import { OrderHeadCreateIbPurchComponent } from './views/components/order-head-create-ib-purch/order-head-create-ib-purch.component';
import { PoFinishComponent } from './views/components/po-finish/po-finish.component';
import { PoFinishProdListComponent } from './views/components/po-finish-prod-list/po-finish-prod-list.component';
import { PoFinishProdWorkComponent } from './views/components/po-finish-prod-work/po-finish-prod-work.component';
import { PoFinishProdWorksComponent } from './views/components/po-finish-prod-works/po-finish-prod-works.component';
import { PohCompAddScanComponent } from './views/components/poh-comp-add-scan/poh-comp-add-scan.component';
import { PohCompAddComponent } from './views/components/poh-comp-add/poh-comp-add.component';
import { PoFinishProdCompsBsComponent } from './views/components/po-finish-prod-comps-bs/po-finish-prod-comps-bs.component';
import { PoFinishProdCompsBComponent } from './views/components/po-finish-prod-comps-b/po-finish-prod-comps-b.component';
import { PoFinishProdCompListAccComponent } from './views/components/po-finish-prod-comp-list-acc/po-finish-prod-comp-list-acc.component';
import { PoFinishPoFinishCompsComponent } from './views/components/po-finish-po-finish-comps/po-finish-po-finish-comps.component';
import { PoFinishProdCompComponent } from './views/components/po-finish-prod-comp/po-finish-prod-comp.component';
import { PoFinishProdCompQtyComponent } from './views/components/po-finish-prod-comp-qty/po-finish-prod-comp-qty.component';
import { StockBatchGetComponent } from './views/components/stock-batch-get/stock-batch-get.component';
import { TracesBComponent } from './views/components/traces-b/traces-b.component';
import { SchedulerComponent } from './core/components/scheduler/scheduler.component';
import { TimeSheetDiaryComponent } from './views/components/time-sheet-diary/time-sheet-diary.component';
import { StockLocationCtrlComponent } from './views/components/stock-location-ctrl/stock-location-ctrl.component';
import { DocArticleTopUserGroupsComponent } from './views/components/doc-article-top-user-groups/doc-article-top-user-groups.component';
import { DocArticleTopUserGroupComponent } from './views/components/doc-article-top-user-group/doc-article-top-user-group.component';
import { DocArticlePorusComponent } from './views/components/doc-article-porus/doc-article-porus.component';
import { ECommerceStatusComponent } from './views/components/e-commerce-status/e-commerce-status.component';
import { InventoryArticleSearchComponent } from './views/components/inventory-article-search/inventory-article-search.component';
import { ProductSettingMcsFormComponent } from './shared/product-setting-mcs-form/product-setting-mcs-form.component';
import { ProductSettingMcsFormInputComponent } from './shared/product-setting-mcs-form/product-setting-mcs-form-input/product-setting-mcs-form-input.component';
import { ProductCreateComponent } from './views/components/product-create/product-create.component';
import { GisPositionAddComponent } from './views/components/gis-position-add/gis-position-add.component';
import { GisPositionComponent } from './views/components/gis-position/gis-position.component';
import { OlmRequisitionComponent } from './views/components/olm-requisition/olm-requisition.component';
import { P2OffDutyDataComponent } from './views/components/p2-off-duty-data/p2-off-duty-data.component';
import { SubOperationsComponent } from './views/components/sub-operations/sub-operations.component';
import { SubOperationComponent } from './views/components/sub-operation/sub-operation.component';
import { SubOperationLangComponent } from './views/components/sub-operation-lang/sub-operation-lang.component';
import { TraceSearchComponent } from './views/components/trace-search/trace-search.component';
import { TimeProjectsWoComponent } from './views/components/time-projects-wo/time-projects-wo.component';
import { TimeRegisterSplitComponent } from './views/components/time-register-split/time-register-split.component';
import { TimeSheetsStatusComponent } from './views/components/time-sheets-status/time-sheets-status.component';
import { TravelBillLogComponent } from './views/components/travel-bill-log/travel-bill-log.component';
import { TravelBillLogsComponent } from './views/components/travel-bill-logs/travel-bill-logs.component';
import { TravelExpenceComponent } from './views/components/travel-expence/travel-expence.component';
import { TravelWageComponent } from './views/components/travel-wage/travel-wage.component';
import { WorkingOrdersComponent } from './views/components/working-orders/working-orders.component';
import { ProductComponent } from './views/components/product/product.component';
import { ProductFunctionsComponent } from './views/components/product/product-functions/product-functions.component';
import { BusinessCoComponent } from './views/components/business-co/business-co.component';
import { ProductSettingComponent } from './views/components/product-setting/product-setting.component';
import { ProductSearchComponent } from './views/components/product-search/product-search.component';
import { CheckListComponent } from './views/components/check-list/check-list.component';
import { HelpDocTtChangedComponent } from './views/components/help-doc-tt-changed/help-doc-tt-changed.component';
import { ProductBasicFormComponent } from './views/components/product/forms/product-basic-form/product-basic-form.component';
import { ProductKpiFormComponent } from './views/components/product/forms/product-kpi-form/product-kpi-form.component';
import { ProductStockFormComponent } from './views/components/product/forms/product-stock-form/product-stock-form.component';
import { ProductPimFormComponent } from './views/components/product/forms/product-pim-form/product-pim-form.component';
import { ProductPurchaseFormComponent } from './views/components/product/forms/product-purchase-form/product-purchase-form.component';
import { ProductProductionFormComponent } from './views/components/product/forms/product-production-form/product-production-form.component';
import { ProductEditComponent } from './views/components/product/product-edit/product-edit.component';
import { ProductEditSaveComponent } from './views/components/product/product-edit-save/product-edit-save.component';
import { WebpageSettingComponent } from './views/components/webpage-setting/webpage-setting.component';
import { WebpageSettingMenuComponent } from './views/components/webpage-setting-menu/webpage-setting-menu.component';
import { IeHttpSrcDirective } from './core/directives/ie-http-src.directive';
import { ImageSrcDirective } from './core/directives/image-src.directive';
import { CustactWorkComponent } from './views/components/custact-work/custact-work.component';
import { PohKeynoTopStatusComponent } from './views/components/poh-keyno-top-status/poh-keyno-top-status.component';
import { PostCalcComponent } from './views/components/post-calc/post-calc.component';
import { PostCalcMComponent } from './views/components/post-calc-m/post-calc-m.component';
import { ProductionStatusComponent } from './views/components/production-status/production-status.component';
import { ConsignmentDeliveryComponent } from './views/components/consignment-delivery/consignment-delivery.component';
import { ConsignmentPlansDeliverySettingsComponent } from './views/components/consignment-plans-delivery-settings/consignment-plans-delivery-settings.component';
import { ConsignmentScanComponent } from './views/components/consignment-scan/consignment-scan.component';
import { ConsignmentPlansDeliveryDriverComponent } from './views/components/consignment-plans-delivery-driver/consignment-plans-delivery-driver.component';
import { ConsignmentPlansDeliveryLabourComponent } from './views/components/consignment-plans-delivery-labour/consignment-plans-delivery-labour.component';
import { ConsignmentPlansDeliveryLocationComponent } from './views/components/consignment-plans-delivery-location/consignment-plans-delivery-location.component';
import { ConsignmentSplitComponent } from './views/components/consignment-split/consignment-split.component';
import { ConsignmentSplitEditComponent } from './views/components/consignment-split-edit/consignment-split-edit.component';
import { ConsignmentCarrierOneComponent } from './views/components/consignment-carrier-one/consignment-carrier-one.component';
import { ComponentReplaceAblesComponent } from './views/components/component-replace-ables/component-replace-ables.component';
import { CustProdAccBcsComponent } from './views/components/cust-prod-acc-bcs/cust-prod-acc-bcs.component';
import { AcLedgerComponent } from './views/components/ac-ledger/ac-ledger.component';
import { AcCustTransComponent } from './views/components/ac-cust-trans/ac-cust-trans.component';
import { SkillsCertificatesComponent } from './views/components/skills-certificates/skills-certificates.component';
import { SkillsCertificateComponent } from './views/components/skills-certificate/skills-certificate.component';
import { OrderHeadInfoFormComponent } from './views/components/order-head/forms/order-head-info-form/order-head-info-form.component';
import { OrderHeadEditComponent } from './views/components/order-head/order-head-edit/order-head-edit.component';
import { OrderHeadAddressFormComponent } from './views/components/order-head/forms/order-head-address-form/order-head-address-form.component';
import { OrderHeadCustomerAddressFormComponent } from './views/components/order-head/forms/order-head-customer-address-form/order-head-customer-address-form.component';
import { OrderHeadDeliveryAddressFormComponent } from './views/components/order-head/forms/order-head-delivery-address-form/order-head-delivery-address-form.component';
import { OrderHeadInvoiceAddressFormComponent } from './views/components/order-head/forms/order-head-invoice-address-form/order-head-invoice-address-form.component';
import { OrderHeadDeliveryFormComponent } from './views/components/order-head/forms/order-head-delivery-form/order-head-delivery-form.component';
import { OrderHeadPriceFormComponent } from './views/components/order-head/forms/order-head-price-form/order-head-price-form.component';
import { SkillsCertificateTableComponent } from './views/components/skills-certificate-table/skills-certificate-table.component';
import { SkillsCertificateTypeComponent } from './views/components/skills-certificate-type/skills-certificate-type.component';
import { TimeStampStartCorrComponent } from './views/components/time-stamp-start-corr/time-stamp-start-corr.component';
import { AcReminderProposalComponent } from './views/components/ac-reminder-proposal/ac-reminder-proposal.component';
import { AccessInfoComponent } from './views/components/access-info/access-info.component';
import { HelpDocSearchComponent } from './views/components/help-doc-search/help-doc-search.component';
import { EdiImportFileUploadComponent } from './views/components/edi-import-file-upload/edi-import-file-upload.component';
import { P2SubscriptionsEditComponent } from './views/components/p2-subscriptions-edit/p2-subscriptions-edit.component';
import { P2ClientUpdateComponent } from './views/components/p2-client-update/p2-client-update.component';
import { P2ClientUpdateEditComponent } from './views/components/p2-client-update-edit/p2-client-update-edit.component';
import { MenuGrpComponent } from './views/components/menu-grp/menu-grp.component';
import { SearchStockComponent } from './views/components/search-stock/search-stock.component';
import { CountersComponent } from './views/components/counters/counters.component';
import { CounterComponent } from './views/components/counter/counter.component';
import { PostCalcWComponent } from './views/components/post-calc-w/post-calc-w.component';
import { FurnitureFabricsSuppliersComponent } from './views/components/furniture-fabrics-suppliers/furniture-fabrics-suppliers.component';
import { DepartmentComponent } from './views/components/department/department.component';
import { BulkMailsComponent } from './views/components/bulk-mails/bulk-mails.component';
import { BulkMailComponent } from './views/components/bulk-mail/bulk-mail.component';
import { TextileModelCategory3sComponent } from './views/components/textile-model-category3s/textile-model-category3s.component';
import { TextileModelCategory3Component } from './views/components/textile-model-category3/textile-model-category3.component';
import { TextileModelCategory3LangComponent } from './views/components/textile-model-category3-lang/textile-model-category3-lang.component';
import { ListsComponent } from './views/components/lists/lists.component';
import { OrderHeadCustomerFormComponent } from './views/components/order-head/forms/order-head-customer-form/order-head-customer-form.component';
import { OrderHeadFunctionsComponent } from './views/components/order-head/order-head-functions/order-head-functions.component';
import { OrderHeadProductionComponent } from './views/components/order-head/order-head-production/order-head-production.component';
import { OrderHeadHoursComponent } from './views/components/order-head/order-head-hours/order-head-hours.component';
import { OrderHeadOrderPicksComponent } from './views/components/order-head/order-head-order-picks/order-head-order-picks.component';
import { OrderHeadDeliveryComponent } from './views/components/order-head/order-head-delivery/order-head-delivery.component';
import { GridMessagesModalComponent } from './core/components/grid/modals/grid-messages-modal/grid-messages-modal.component';
import { ListDatasComponent } from './views/components/list-datas/list-datas.component';
import { ListDataComponent } from './views/components/list-data/list-data.component';
import { ListDataLangComponent } from './views/components/list-data-lang/list-data-lang.component';
import { ListNameComponent } from './views/components/list-name/list-name.component';
import { OrderHeadEmailsComponent } from './views/components/order-head/order-head-emails/order-head-emails.component';
import { OrderHeadCustactAcComponent } from './views/components/order-head/order-head-custact-ac/order-head-custact-ac.component';
import { OrderHeadDynamicPostCalcComponent } from './views/components/order-head/order-head-dynamic-post-calc/order-head-dynamic-post-calc.component';
import { OrderHeadConvertToOrderButtonComponent } from './views/components/order-head/order-head-convert-to-order-button/order-head-convert-to-order-button.component';
import { OrderHeadCancelDialogComponent } from './views/components/order-head/order-head-cancel-dialog/order-head-cancel-dialog.component';
import { PoFinishProdCompListAccsComponent } from './views/components/po-finish-prod-comp-list-accs/po-finish-prod-comp-list-accs.component';
import { SelectNewDocTypeComponent } from './views/components/select-new-doc-type/select-new-doc-type.component';
import { WebSiteNewComponent } from './views/components/web-site-new/web-site-new.component';
import { PurchRequestCreateComponent } from './views/components/purch-request-create/purch-request-create.component';
import { RequisitionSimplifiedComponent } from './views/components/requisition-simplified/requisition-simplified.component';
import { RequisitionSimplifiedFiltersComponent } from './views/components/requisition-simplified/requisition-simplified-filters/requisition-simplified-filters.component';
import { OrderHeadChoicesComponent } from './views/components/order-head-choices/order-head-choices.component';
import { OrderHeadChoiceComponent } from './views/components/order-head-choice/order-head-choice.component';
import { OrderHeadCreateProjectComponent } from './views/components/order-head-create-project/order-head-create-project.component';
import { ProjectActivityTypesComponent } from './views/components/project-activity-types/project-activity-types.component';
import { ProjectActivityTypeComponent } from './views/components/project-activity-type/project-activity-type.component';
import { OrderCustComComponent } from './views/components/order-cust-com/order-cust-com.component';
import { OeeDashBoardComponent } from './views/components/oee-dash-board/oee-dash-board.component';
import { EquipmentEditComponent } from './views/components/equipment-edit/equipment-edit.component';
import { CustactPlansComponent } from './views/components/custact-plans/custact-plans.component';
import { CategoryLinksComponent } from './views/components/category-links/category-links.component';
import { CheckListItemTwsComponent } from './views/components/check-list-item-tws/check-list-item-tws.component';
import { CheckListItemTwComponent } from './views/components/check-list-item-tw/check-list-item-tw.component';
import { CustactCalcPickComponent } from './views/components/custact-calc-pick/custact-calc-pick.component';
import { OrderHeadOrderLineCalculationComponent } from './views/components/order-head/order-head-order-line-calculation/order-head-order-line-calculation.component';
import { OrderHeadOrderLinesComponent } from './views/components/order-head/order-head-order-lines/order-head-order-lines.component';
import { DeliveryMethodComponent } from './views/components/delivery-method/delivery-method.component';
import { DispatchPlanningBlockOrderComponent } from './views/components/dispatch-planning-block-order/dispatch-planning-block-order.component';
import { EdiExportComponent } from './views/components/edi-export/edi-export.component';
import { EdiImportComponent } from './views/components/edi-import/edi-import.component';
import { InterBranchInvoiceLineComponent } from './views/components/inter-branch-invoice-line/inter-branch-invoice-line.component';
import { InvoiceHeadEhfComponent } from './views/components/invoice-head-ehf/invoice-head-ehf.component';
import { InvoiceMakeMultiComponent } from './views/components/invoice-make-multi/invoice-make-multi.component';
import { JobScheduleComponent } from './views/components/job-schedule/job-schedule.component';
import { InvoiceHeadComponent } from './views/components/invoice-head/invoice-head.component';
import { SelectReportComponent } from './views/components/select-report/select-report.component';
import { ProductMoveChoicesComponent } from './views/components/product-move-choices/product-move-choices.component';
import { ProductMoveChoiceComponent } from './views/components/product-move-choice/product-move-choice.component';
import { ProductDataHarvestComponent } from './views/components/product-data-harvest/product-data-harvest.component';
import { ComponentComponent } from './views/components/component/component.component';
import { SellerComponent } from './views/components/seller/seller.component';
import { SellersComponent } from './views/components/sellers/sellers.component';
import { ComponentCalculationComponent } from './views/components/component/component-calculation/component-calculation.component';
import { ProductionRoutingAddComponent } from './views/components/production-routing-add/production-routing-add.component';
import { ComponentCalculationGridModalComponent } from './modal/component-calculation-grid-modal/component-calculation-grid-modal.component';
import { StockStatusComponent } from './views/components/stock-status/stock-status.component';
import { StockStatusDrillDownComponent } from './views/components/stock-status-drill-down/stock-status-drill-down.component';
import { CategoryDatasComponent } from './views/components/category-datas/category-datas.component';
import { CategoryDataSelectComponent } from './views/components/category-data-select/category-data-select.component';
import { BrRegComponent } from './views/components/br-reg/br-reg.component';
import { TransportAreaPpMoveComponent } from './views/components/transport-area-pp-move/transport-area-pp-move.component';
import { BrRegViewComponent } from './views/components/br-reg-view/br-reg-view.component';
import { PoJobLaboursComponent } from './views/components/po-job-labours/po-job-labours.component';
import { AiChatComposerComponent } from './core/components/ai-chat/ai-chat-composer/ai-chat-composer.component';
import { AiChatComponent } from './core/components/ai-chat/ai-chat.component';
import { AiChatConversationListComponent } from './core/components/ai-chat/ai-chat-conversation-list/ai-chat-conversation-list.component';
import { AiChatConversationComponent } from './core/components/ai-chat/ai-chat-conversation/ai-chat-conversation.component';
import { AiChatMessageComponent } from './core/components/ai-chat/ai-chat-message/ai-chat-message.component';
import { AiChatHeaderComponent } from './core/components/ai-chat/ai-chat-header/ai-chat-header.component';
import { AiChatPanelComponent } from './core/components/ai-chat/ai-chat-panel.component';
import { AiChatThinkingComponent } from './core/components/ai-chat/ai-chat-thinking/ai-chat-thinking.component';
import { AiChatToolCallsDialogComponent } from './core/components/ai-chat/ai-chat-tool-calls-dialog/ai-chat-tool-calls-dialog.component';
import { AiChatWorkspaceComponent } from './core/components/ai-chat/ai-chat-workspace/ai-chat-workspace.component';
import { AccountingBuntComponent } from './views/components/accounting-bunt/accounting-bunt.component';
import { CalculationSeasonYearComponent } from './views/components/calculation-season-year/calculation-season-year.component';
import { CalculationOverviewComponent } from './views/components/calculation-overview/calculation-overview.component';
import { DomObjectComponent } from './views/components/dom-object/dom-object.component';
import { ChoicePossRuleComponent } from './views/components/choice-poss-rule/choice-poss-rule.component';
import { ChoicePossRulesComponent } from './views/components/choice-poss-rules/choice-poss-rules.component';
import { ChoicePossRulesRunComponent } from './views/components/choice-poss-rules-run/choice-poss-rules-run.component';
import { BankReconciliationAnalysisComponent } from './views/components/bank-reconciliation-analysis/bank-reconciliation-analysis.component';
import { BusinessCoOnBoardingComponent } from './views/components/business-co-on-boarding/business-co-on-boarding.component';
import { CampaignsComponent } from './views/components/campaigns/campaigns.component';
import { CampaignEditComponent } from './views/components/campaign-edit/campaign-edit.component';
import { CampaignProductsComponent } from './views/components/campaign-products/campaign-products.component';
import { BusinessCoAccountingComponent } from './views/components/business-co-accounting/business-co-accounting.component';
import { AcVoucherTypesComponent } from './views/components/ac-voucher-types/ac-voucher-types.component';
import { AcRemLtrComponent } from './views/components/ac-rem-ltr/ac-rem-ltr.component';
import { BusinessCoBankAccountComponent } from './views/components/business-co-bank-account/business-co-bank-account.component';
import { ForecastDataGridComponent } from './views/components/forecast-data-grid/forecast-data-grid.component';
import { SpecialCustProdCalcComponent } from './views/components/special-cust-prod-calc/special-cust-prod-calc.component';
import { FindCustomersComponent } from './views/components/find-customers/find-customers.component';
import { FindCustomersGridComponent } from './views/components/find-customers/find-customers-grid/find-customers-grid.component';
import { FindCustomersListComponent } from './views/components/find-customers/find-customers-list/find-customers-list.component';
import { CsvExportArgComponent } from './views/components/csv-export-arg/csv-export-arg.component';
import { CsvExportsComponent } from './views/components/csv-exports/csv-exports.component';
import { CustactCalcJobExitComponent } from './views/components/custact-calc-job-exit/custact-calc-job-exit.component';
import { PohTopOpComponent } from './views/components/poh-top-op/poh-top-op.component';
import { PoPurchPlanningComponent } from './views/components/po-purch-planning/po-purch-planning.component';
import { AcCustTransesComponent } from './views/components/ac-cust-transes/ac-cust-transes.component';
import { AcSuppTransesComponent } from './views/components/ac-supp-transes/ac-supp-transes.component';
import { PoConnectedPositionsComponent } from './views/components/po-connected-positions/po-connected-positions.component';
import { PicturesComponent } from './views/components/pictures/pictures.component';
import { PictureCardComponent } from './views/components/pictures/picture-card/picture-card.component';
import { DocArcComponent } from './views/components/doc-arc/doc-arc.component';
import { DocArcInformationComponent } from './views/components/doc-arc/doc-arc-information/doc-arc-information.component';
import { PicturesListComponent } from './views/components/pictures/pictures-list/pictures-list.component';
import { OrderHeadCapJobComponent } from './views/components/order-head-cap-job/order-head-cap-job.component';
import { AcReportLayoutMenuComponent } from './views/components/ac-report-layout-menu/ac-report-layout-menu.component';
import { P2ExportProcComponent } from './views/components/p2-export-proc/p2-export-proc.component';
import { P2ReleaseNoteComponent } from './views/components/p2-release-note/p2-release-note.component';
import { P2ReleaseNoteEditComponent } from './views/components/p2-release-note-edit/p2-release-note-edit.component';
import { P2TableComponent } from './views/components/p2-table/p2-table.component';
import { P2TableEditComponent } from './views/components/p2-table-edit/p2-table-edit.component';
import { P2UpdateTablesComponent } from './views/components/p2-update-tables/p2-update-tables.component';
import { P2UpdateTableEditComponent } from './views/components/p2-update-table-edit/p2-update-table-edit.component';
import { P2UpdateProceduresComponent } from './views/components/p2-update-procedures/p2-update-procedures.component';
import { P2UpdateProcedureEditComponent } from './views/components/p2-update-procedure-edit/p2-update-procedure-edit.component';
import { P2WebpageComponent } from './views/components/p2-webpage/p2-webpage.component';
import { P2WebpageEditComponent } from './views/components/p2-webpage-edit/p2-webpage-edit.component';
import { P2WebpageLinkEditComponent } from './views/components/p2-webpage-link-edit/p2-webpage-link-edit.component';
import { P2DataTaskComponent } from './views/components/p2-data-task/p2-data-task.component';
import { P2DataTaskEditComponent } from './views/components/p2-data-task-edit/p2-data-task-edit.component';
import { P2SubscriptionComponent } from './views/components/p2-subscription/p2-subscription.component';
import { P2SubscriptionEditComponent } from './views/components/p2-subscription-edit/p2-subscription-edit.component';
import { P2ReportDefComponent } from './views/components/p2-report-def/p2-report-def.component';
import { P2ReportDefsComponent } from './views/components/p2-report-defs/p2-report-defs.component';
import { P2NotificationsComponent } from './views/components/p2-notifications/p2-notifications.component';
import { NiComponent } from './views/components/ni/ni.component';
import { BspComponent } from './views/components/bsp/bsp.component';
import { BspMenuComponent } from './views/components/bsp-menu/bsp-menu.component';
import { BspSetupComponent } from './views/components/bsp-setup/bsp-setup.component';
import { BspSubComponent } from './views/components/bsp-sub/bsp-sub.component';
import { BspUgComponent } from './views/components/bsp-ug/bsp-ug.component';
import { CarrierComponent } from './views/components/carrier/carrier.component';
import { CarriersComponent } from './views/components/carriers/carriers.component';
import { VatReportMenuComponent } from './views/components/vat-report-menu/vat-report-menu.component';
import { VatReportSubmissionStepsComponent } from './views/components/vat-report-menu/vat-report-submission-steps/vat-report-submission-steps.component';
import { CsvImportComponent } from './views/components/csv-import/csv-import.component';
import { DiaryComponent } from './views/components/diary/diary.component';
import { DataMiningComponent } from './views/components/data-mining/data-mining.component';
import { GanttButtonComponent } from './core/components/gantt/gantt-button/gantt-button.component';
import { GanttToggleComponent } from './core/components/gantt/gantt-toggle/gantt-toggle.component';
import { CapTasksViewComponent } from './views/components/cap-tasks-view/cap-tasks-view.component';
import { CapTaskResourceComponent } from './views/components/cap-task-resource/cap-task-resource.component';
import { CaseComponent } from './views/components/case/case.component';
import { VtComponent } from './views/components/vt/vt.component';
import { VtLinkComponent } from './views/components/vt-link/vt-link.component';
import { VtsComponent } from './views/components/vts/vts.component';
import { WorkComponent } from './views/components/work/work.component';
import { SalaryTypeComponent } from './views/components/salary-type/salary-type.component';
import { TravelBillComponent } from './views/components/travel-bill/travel-bill.component';
import { TranslateComponent } from './views/components/translate/translate.component';
import { TranslateEditPageComponent } from './views/components/translate-edit/translate-edit.component';
import { TempstockComponent } from './views/components/tempstock/tempstock.component';
import { ShiftComponent } from './views/components/shift/shift.component';
import { ShiftDayComponent } from './views/components/shift-day/shift-day.component';
import { ShiftDayBreakComponent } from './views/components/shift-day-break/shift-day-break.component';
import { ShiftDayTimeComponent } from './views/components/shift-day-time/shift-day-time.component';
import { ShiftPlanComponent } from './views/components/shift-plan/shift-plan.component';
import { ShiftPlansComponent } from './views/components/shift-plans/shift-plans.component';
import { ShiftsComponent } from './views/components/shifts/shifts.component';
import { ShiftViewComponent } from './views/components/shift-view/shift-view.component';
import { SrCrewComponent } from './views/components/sr-crew/sr-crew.component';
import { SrCrewMenuComponent } from './views/components/sr-crew-menu/sr-crew-menu.component';
import { SrCrewResourceComponent } from './views/components/sr-crew-resource/sr-crew-resource.component';
import { SrCrewResourceViewComponent } from './views/components/sr-crew-resource-view/sr-crew-resource-view.component';
import { SrCrewsComponent } from './views/components/sr-crews/sr-crews.component';
import { SrCheckListCrewChangeComponent } from '@app/views/components/sr-check-list-crew-change/sr-check-list-crew-change.component';
import { SrCheckListCrewsComponent } from '@app/views/components/sr-check-list-crews/sr-check-list-crews.component';
import { SrCheckListEquipmentComponent } from '@app/views/components/sr-check-list-equipment/sr-check-list-equipment.component';
import { SrCheckListEquipmentsComponent } from '@app/views/components/sr-check-list-equipments/sr-check-list-equipments.component';
import { SrCheckListExpenseComponent } from '@app/views/components/sr-check-list-expense/sr-check-list-expense.component';
import { SrCheckListExpensesComponent } from '@app/views/components/sr-check-list-expenses/sr-check-list-expenses.component';
import { SrCheckListLdComponent } from '@app/views/components/sr-check-list-ld/sr-check-list-ld.component';
import { SrCheckListLdsComponent } from '@app/views/components/sr-check-list-lds/sr-check-list-lds.component';
import { SrOperationComponent } from '@app/views/components/sr-operation/sr-operation.component';
import { SrOperationAddrComponent } from '@app/views/components/sr-operation-addr/sr-operation-addr.component';
import { SrOperationArComponent } from '@app/views/components/sr-operation-ar/sr-operation-ar.component';
import { SrOperationManualAddComponent } from '@app/views/components/sr-operation-manual-add/sr-operation-manual-add.component';
import { SrOperationResourceComponent } from '@app/views/components/sr-operation-resource/sr-operation-resource.component';
import { SrOperationsComponent } from '@app/views/components/sr-operations/sr-operations.component';
import { FossProductionComponent } from '@app/views/components/foss-production/foss-production.component';
import { FactsComponent } from '@app/views/components/facts/facts.component';
import { FactseditComponent } from '@app/views/components/factsedit/factsedit.component';
import { EdiMenuComponent } from '@app/views/components/edi-menu/edi-menu.component';
import { EdiOrderHeadsComponent } from '@app/views/components/edi-order-heads/edi-order-heads.component';
import { EdiSetupComponent } from '@app/views/components/edi-setup/edi-setup.component';
import { EdiSetupModalComponent } from '@app/modal/edi-setup-modal/edi-setup-modal.component';
import { CustactGanttComponent } from './views/components/custact-gantt/custact-gantt.component';
import { GenericTerminalScanAComponent } from '@app/views/components/generic-terminal-scan-a/generic-terminal-scan-a.component';
import { ExcelImportComponent } from '@app/views/components/excel-import/excel-import.component';
import { GridExportArgComponent } from '@app/views/components/grid-export-arg/grid-export-arg.component';
import { OeeDataComponent } from '@app/views/components/oee-data/oee-data.component';
import { OeeDataCurrentComponent } from '@app/views/components/oee-data-current/oee-data-current.component';
import { OeeMenuComponent } from '@app/views/components/oee-menu/oee-menu.component';
import { SelectOeeStateComponent } from '@app/views/components/select-oee-state/select-oee-state.component';
import { SelectOeeStateRemarkComponent } from '@app/views/components/select-oee-state-remark/select-oee-state-remark.component';
import { SelectLabourPuComponent } from '@app/views/components/select-labour-pu/select-labour-pu.component';
import { ShipmentComponent } from '@app/views/components/shipment/shipment.component';
import { RiskElementComponent } from '@app/views/components/risk-element/risk-element.component';
import { RiskElementsComponent } from '@app/views/components/risk-elements/risk-elements.component';
import { UsersComponent } from '@app/views/components/users/users.component';
import { SelectProductComponent } from '@app/views/components/select-product/select-product.component';
import { SelectPoFinishComponent } from '@app/views/components/select-po-finish/select-po-finish.component';
import { SelectProdsizeComponent } from '@app/views/components/select-prodsize/select-prodsize.component';
import { SelectProdChoiceComponent } from '@app/views/components/select-prod-choice/select-prod-choice.component';
import { SelectWorkingOrderComponent } from '@app/views/components/select-working-order/select-working-order.component';
import { SelectCustomerComponent } from '@app/views/components/select-customer/select-customer.component';
import { SelectCityComponent } from '@app/views/components/select-city/select-city.component';
import { SelectLabourComponent } from '@app/views/components/select-labour/select-labour.component';
import { ConsignmentsComponent } from './views/components/consignments/consignments.component';
import { ConsignmentSerialNumberComponent } from './views/components/consignment-serial-number/consignment-serial-number.component';
import { SrUserComponent } from '@app/views/components/sr-user/sr-user.component';
import { SrUserAdminComponent } from '@app/views/components/sr-user-admin/sr-user-admin.component';
import { SrUsersComponent } from '@app/views/components/sr-users/sr-users.component';
import { SrEquipmentComponent } from '@app/views/components/sr-equipment/sr-equipment.component';
import { SrEquipmentsComponent } from '@app/views/components/sr-equipments/sr-equipments.component';
import { SrEquipmentResourceViewComponent } from '@app/views/components/sr-equipment-resource-view/sr-equipment-resource-view.component';
import { SrResourceComponent } from '@app/views/components/sr-resource/sr-resource.component';
import { SrResourceViewComponent } from '@app/views/components/sr-resource-view/sr-resource-view.component';
import { SrFollowUpComponent } from '@app/views/components/sr-follow-up/sr-follow-up.component';
import { SrFollowUpForwardComponent } from '@app/views/components/sr-follow-up-forward/sr-follow-up-forward.component';
import { SrFollowUpsComponent } from '@app/views/components/sr-follow-ups/sr-follow-ups.component';
import { SrNotCompletedComponent } from '@app/views/components/sr-not-completed/sr-not-completed.component';
import { SrNotCompletedNewComponent } from '@app/views/components/sr-not-completed-new/sr-not-completed-new.component';
import { SrRefundBasisComponent } from '@app/views/components/sr-refund-basis/sr-refund-basis.component';
import { SrExpensesComponent } from '@app/views/components/sr-expenses/sr-expenses.component';
import { SrTravelBillComponent } from '@app/views/components/sr-travel-bill/sr-travel-bill.component';
import { SrTravelBillSpecComponent } from '@app/views/components/sr-travel-bill-spec/sr-travel-bill-spec.component';
import { SrReportMenuComponent } from '@app/views/components/sr-report-menu/sr-report-menu.component';
import { SrActivityComponent } from '@app/views/components/sr-activity/sr-activity.component';
import { SrFindReportComponent } from '@app/views/components/sr-find-report/sr-find-report.component';
import { UsergroupSettingComponent } from '@app/views/components/usergroup-setting/usergroup-setting.component';
import { UsergroupSettingEditComponent } from '@app/views/components/usergroup-setting-edit/usergroup-setting-edit.component';
import { UsergroupTableComponent } from '@app/views/components/usergroup-table/usergroup-table.component';
import { UsergroupTableEditComponent } from '@app/views/components/usergroup-table-edit/usergroup-table-edit.component';
import { SrRefundCreateComponent } from '@app/views/components/sr-refund-create/sr-refund-create.component';
import { SrRefundInputCrewComponent } from '@app/views/components/sr-refund-input-crew/sr-refund-input-crew.component';
import { SrCustomerEditComponent } from '@app/views/components/sr-customer-edit/sr-customer-edit.component';
import { SrRefundListComponent } from '@app/views/components/sr-refund-list/sr-refund-list.component';
import { ConsignmentLineAssignComponent } from './views/components/consignment-line-assign/consignment-line-assign.component';
import { CustomerBonusComponent } from './views/components/customer-bonus/customer-bonus.component';
import { ConsignmentComponent } from './views/components/consignment/consignment.component';
import { AiSettingsComponent } from './views/components/ai-settings/ai-settings.component';
import { AiSettingsProvidersSetupComponent } from './views/components/ai-settings/ai-settings-providers-setup/ai-settings-providers-setup.component';
import { AiSettingsModelsComponent } from './views/components/ai-settings/ai-settings-models/ai-settings-models.component';
import { WebpageSettingEditComponent } from './views/components/webpage-setting-edit/webpage-setting-edit.component';
import { WebpageSubscriptionEditComponent } from './views/components/webpage-subscription-edit/webpage-subscription-edit.component';

@NgModule({
    // @ts-ignore: TS2590 union type too complex
    declarations: [
        AppComponent,
        BjsTestComponent,
        JlrTestComponent,
        WordlangDialogComponent,
        CardComponent,
        PageComponent,
        ResponsiveBreakpointsComponent,
        OutsideClickDirective,
        InputNumberComponent,
        FormFieldBaseComponent,
        FormFieldButtonsComponent,
        CheckboxComponent,
        ListboxComponent,
        ListboxHighlight,
        BankReconciliationFilter,
        ListboxPopupDirective,
        SearchComponent,
        FormFieldButtonComponent,
        TabsComponent,
        DatetimeComponent,
        MultiSelectComponent,
        PasswordInputComponent,
        FieldGroupComponent,
        ContextMenuContainerComponent,
        ContextMenuComponent,
        ContextMenuItemComponent,
        OverflowMenuComponent,
        OverflowMenuItemComponent,
        PageHeadingComponent,
        FileUploaderComponent,
        SelectComponent,
        SelectOptionComponent,
        TextareaComponent,
        ButtonComponent,
        GridComponent,
        GridLoadingOverlayComponent,
        DateTimeCellEditorComponent,
        LookupCellEditorComponent,
        NumberCellEditorComponent,
        CheckboxFloatingFilterComponent,
        SymbolCellRendererComponent,
        ColumnLayoutModalComponent,
        FileUploaderComponent,
        GridFunctionsCellRendererComponent,
        PrintModalComponent,
        ConfirmModalComponent,
        LinkCellRendererComponent,
        ProgressBarComponent,
        ProgressModalComponent,
        ButtonSaveComponent,
        ButtonMultiComponent,
        CheckboxColumnHeaderComponent,
        PopoverComponent,
        PopoverDirective,
        InputComponent,
        ThumbnailCellRendererComponent,
        RowEditModalComponent,
        PopupTableComponent,
        DateCellEditorComponent,
        DateFloatingFilterComponent,
        DateFilterSelectionComponent,
        ErrorModalComponent,
        MainMenuComponent,
        StudentTestComponent,
        KanbanComponent,
        SignaturePadModalComponent,
        SignaturePadComponent,
        BankReconciliationComponent,
        BankReconciliationItemComponent,
        GridBookingComponent,
        TtInputButtonsDirective,
        MyActivitiesComponent,
        TtListDirective,
        ImInputDirective,
        SettingsComponent,
        HtmlReportComponent,
        ContextMenuReportComponent,
        HtmlProcEditorComponent,
        MessageDialogComponent,
        SirvSetupComponent,
        PictureBankComponent,
        PictureBanksComponent,
        DateInputComponent,
        DateComponent,
        ListItemComponent,
        ListComponent,
        ListFilterPipe,
        ListSortPipe,
        ListFilterDirective,
        ListCheckboxSelectionDirective,
        ListRadioSelectionDirective,
        TimeInputComponent,
        TimeComponent,
        ButtonGotoComponent,
        TabComponent,
        ButtonSettingsComponent,
        ImageComponent,
        ContentSwitcherComponent,
        SwitchComponent,
        DateRangeComponent,
        DividerComponent,
        DividerRowsComponent,
        DividerColumnsComponent,
        DividerPaneComponent,
        BusinesscoSettingSmtpComponent,
        WebpagemenuComponent,
        WebpagemenuGroupWidgetComponent,
        EconomyMenuComponent,
        ProductionMenuComponent,
        CrmMenuComponent,
        ConsignmentMenuComponent,
        StockMenuComponent,
        ProductMenuComponent,
        PurchaseMenuComponent,
        EcommerceMenuComponent,
        TimestampMenuComponent,
        ExternalProductionMenuComponent,
        ToolsMenuComponent,
        DocarticleMenuComponent,
        MyPageComponent,
        DevelopMenuComponent,
        HrMenuComponent,
        ChangePasswordDialogComponent,
        TranslateWordDirective,
        WebpagemenuDashboardComponent,
        WebpagemenuDashboardWidgetComponent,
        P2DatataskLogsComponent,
        P2DatataskLogComponent,
        PrettyJsonPipe,
        CheckboxListComponent,
        DispatchPlanningComponent,
        ColorInputComponent,
        DashboardWidgetBaseComponent,
        DashboardWidgetBasicComponent,
        DashboardWidgetChartComponent,
        DashboardWidgetGridComponent,
        DashboardChartModalComponent,
        KpiCompComponent,
        SaftExportComponent,
        GdprDialogComponent,
        TranslateEditComponent,
        DeviationSettingsComponent,
        DeviationTypeComponent,
        DeviationSourceComponent,
        TranslateEditDialogComponent,
        DeviationSource2Component,
        DeviationSource2ViewComponent,
        DeviationSourceViewComponent,
        DeviationTypeViewComponent,
        CalculatorModalComponent,
        ProductSettingMcItemComponent,
        P2UpdateScriptEditComponent,
        P2UpdateScriptEditViewComponent,
        P2UpdateScriptsComponent,
        P2UpdateScriptEditDialogComponent,
        ToastComponent,
        SliderComponent,
        TtNumpadLegacyComponent,
        TtInputLegacyComponent,
        ConsignmentPlansDeliveryComponent,
        SliderComponent,
        AccountingBsComponent,
        BottomSheetComponent,
        ConsignmentPlansConsignmentsComponent,
        TtScreenBlockComponent,
        DashboardSlicerWidgetBaseComponent,
        KendoBaseComponent,
        EditorComponent,
        TreeViewComponent,
        PurchReceivalPlanningComponent,
        DocArticleEditComponent,
        CommentComponent,
        ChatComponent,
        CustactDialogsComponent,
        PickJobGroupComponent,
        LabelTranslationDialogComponent,
        InterbranchEquipmentInvoicingComponent,
        ChecklistItemComponent,
        PlannerComponent,
        PlannerResourceFilter,
        PlannerEventComponent,
        HelpdocComponent,
        VideoTutorialsComponent,
        VideoTutorialComponent,
        MetaColumnItemComponent,
        AccountingProcessingComponent,
        AccountingProcessingHeadComponent,
        ProductsComponent,
        ProductBomComponent,
        MediaQueryPipe,
        EmailComponent,
        EmailArchiveFormComponent,
        ConcernOrdersGridComponent,
        DocumentViewerLegacyDirective,
        DocumentViewerComponent,
        OfficeDocumentViewerComponent,
        EmailViewerComponent,
        EmailViewComponent,
        SearchMultiSelect,
        SideDrawerContainerComponent,
        SideDrawerComponent,
        OrderheadExternalCostComponent,
        SpreadsheetComponent,
        PurchPlanningOverviewComponent,
        FormatDatePipe,
        ProductsOverviewComponent,
        AccountproductComponent,
        OrderPurchComponent,
        TravelWageMenuComponent,
        FileDataViewerComponent,
        EditDocarcDocumentCategoryDialogComponent,
        DocumentViewerGridDialogComponent,
        DeviantVatComponent,
        DocumentsComponent,
        CustactCalcComponent,
        AboutComponent,
        AboutViewComponent,
        GridDrillDownComponent,
        PrognosisPurchaseOverviewComponent,
        P2SettingEditFormComponent,
        P2SettingEditViewComponent,
        P2SettingsComponent,
        ProductsUnitTooltipComponent,
        GridHtmlTooltipComponent,
        AddExistingDocArcComponent,
        AddExistingDocArcDialogComponent,
        ActStatusesComponent,
        ActivityTypeActStatusesComponent,
        ActStatusMenuComponent,
        DocArticlesComponent,
        DocArticleComponent,
        DocArticleEditViewComponent,
        DocArticleViewComponent,
        ActivityTypeActStatusComponent,
        ActivityTypeLangsComponent,
        ActStatusLangsComponent,
        ActivityTypesComponent,
        ActivityTypeDevTypesComponent,
        PrognosisPurchaseHistoryComponent,
        FieldContainerComponent,
        OrderDialogDialogComponent,
        OrderHeadDynamicSpecialComponent,
        ActivityTypeDevTypeComponent,
        OrderheadDialogComponent,
        AcReportLayoutsComponent,
        AcReportLayoutComponent,
        AcReportLayoutLineComponent,
        BusinesscoSettingsComponent,
        BusinessSitesComponent,
        BusinessSiteComponent,
        StockPlacesToComponent,
        StockPlaceDelTimeComponent,
        PickJobGrpsComponent,
        TransportAreasComponent,
        TransportAreaMenuComponent,
        TransportAreaComponent,
        TransportAreaPpAddComponent,
        ProjectComponent,
        ProjectViewComponent,
        StockPlaceDelTimesComponent,
        CategoryListMainComponent,
        OrderLineCreditCausesComponent,
        OrderLineCreditCauseComponent,
        OrderLineCreditCauseLangComponent,
        TransportAreaPpComponent,
        ProjectMenuComponent,
        ProjectsComponent,
        ProjectMenuViewComponent,
        StateModalComponent,
        SideNavComponent,
        SideNavItemComponent,
        SideNavFilterPipe,
        BaseCurrenciesComponent,
        CurrencyTypesComponent,
        CurrencyExRatesComponent,
        CurrencyExRateHistComponent,
        CurrencyExRateComponent,
        CustGrpsComponent,
        CustGrpMenuComponent,
        CustGrpLangsComponent,
        ValutaComponent,
        CustomsTarifsComponent,
        CustomsTarifMenuComponent,
        CustomsTarifLangComponent,
        CustomsTarifComponent,
        DbStationComponent,
        StockPlacesComponent,
        DeliveryMethodsComponent,
        DeliveryMethodServicesComponent,
        TranscarrierServiceActiveComponent,
        DeliveryMethodLangsComponent,
        DeliveryTermsComponent,
        DeliveryTermComponent,
        DeliveryTermLangsComponent,
        NavBarComponent,
        FavouritesDialogComponent,
        DepartmentsComponent,
        DepartmentMenuComponent,
        DepartmentLangComponent,
        DiscGrpCustsComponent,
        DiscGrpCustComponent,
        DiscGrpCustLangsComponent,
        DiscGrpProdsComponent,
        DiscGrpProdComponent,
        DiscGrpProdLangsComponent,
        DiscGrpDatasComponent,
        DocCategoriesComponent,
        DocCategoryComponent,
        DocCategoryLangComponent,
        DoDataImpsWizardComponent,
        DocPackTypesComponent,
        DocPackTypeMenuComponent,
        DocPackTypeComponent,
        DocPackTypeChapterComponent,
        EmailTemplatesComponent,
        EmailTemplateComponent,
        FabricsGrpsComponent,
        FabricsGrpComponent,
        FactoriesComponent,
        FactoryComponent,
        FactoryMenuComponent,
        OperationFactoriesComponent,
        OperationFactoryComponent,
        EquipmentListComponent,
        OeeStateEquipmentsComponent,
        OeeStateEquipmentComponent,
        OeeStateRemarksComponent,
        OeeStateRemarkComponent,
        ConsignmentPlansConsignmentContentComponent,
        DocumentsMergedPreviewComponent,
        WorkGrpsComponent,
        WorkGrpMenuComponent,
        WorkGrpComponent,
        EquipmentGrpsComponent,
        EquipmentGrpMenuComponent,
        EquipmentGrpLangComponent,
        EquipmentGrpComponent,
        FactoringsComponent,
        FactoringComponent,
        ForecastsComponent,
        ForecastComponent,
        FreightRateTypesComponent,
        FreightRateTypeComponent,
        FtpAccountsComponent,
        FtpAccountComponent,
        FurnitureFabricsSComponent,
        ReportPreviewComponent,
        ReportPreviewDialogComponent,
        EmailCreateComponent,
        FurnitureFabricsMenuComponent,
        FurnitureFabricsColorsComponent,
        FurnitureFabricsColorComponent,
        AccountsComponent,
        AccountComponent,
        BusinessSComponent,
        BusinessMenuComponent,
        BusinessLangComponent,
        BusinessComponent,
        InterbranchMultiComponent,
        InterbranchMultiGetComponent,
        InterbranchLevelComponent,
        InvoiceMethodsComponent,
        InvoiceMethodComponent,
        InvoiceMethodLangsComponent,
        KpiCompsComponent,
        LanguagesComponent,
        LanguageLangsComponent,
        LanguageComponent,
        CarrierListComponent,
        ModelsComponent,
        ModelLangsComponent,
        ModelMenuComponent,
        ModelComponent,
        OperationMenuComponent,
        OperationLangsComponent,
        OperationComponent,
        OrderLineCancelCausesComponent,
        OrderLineCancelCauseComponent,
        OrderLineCancelCauseLangComponent,
        PersFactsComponent,
        PersFactsEditComponent,
        RadioButtonGroupComponent,
        RadioButtonComponent,
        ProductsConfigComponent,
        ProductsConfigViewComponent,
        MyPageCustomWidgetComponent,
        MetaTableViewsComponent,
        MetaTableViewComponent,
        MetaTableMenuComponent,
        MetaTableComponent,
        MetaColumnsComponent,
        MetaColumnComponent,
        MetaTablesComponent,
        ProdBeoMethodsComponent,
        PriceIndexesComponent,
        PriceIndexComponent,
        PriceIndexMenuComponent,
        ProductSettingsComponent,
        ProductSettingMcComponent,
        ProdGrpsComponent,
        ProdGrpComponent,
        PurchHeadComponent,
        PurchHeadHeadComponent,
        DocumentsGridComponent,
        SettingsToolPanelComponent,
        ProdGrpLangsComponent,
        ProdGrp2Component,
        ProdGrp2MenuComponent,
        ProdGrp3MenuComponent,
        ProdGrp4MenuComponent,
        ProdGrp4Component,
        TextileModelCategory6sComponent,
        TextileModelCategory6Component,
        TextileModelCategory6LangComponent,
        ProductCategorysComponent,
        ProductCategoryComponent,
        ProductCategoryLangComponent,
        ProductCompositionsComponent,
        ProductCompositionComponent,
        ProductCompositionLangComponent,
        TextileModelCategory5sComponent,
        TextileModelCategory5Component,
        TextileModelCategory5LangComponent,
        TextileModelCategory4sComponent,
        TextileModelCategory4Component,
        TextileModelCategory4LangComponent,
        TextileModelCategory7sComponent,
        TextileModelCategory7Component,
        TextileModelCategory7LangComponent,
        TextileModelCategory8sComponent,
        TextileModelCategory8Component,
        TextileModelCategory8LangComponent,
        ProdBeoMethodLangsComponent,
        PurchHeadPurchLinesComponent,
        PurchHeadFunctionsComponent,
        PurchHeadViewComponent,
        EmailGridComponent,
        ProductionRoutingsComponent,
        ProductionRoutingMenuComponent,
        ProductionRoutingComponent,
        OperationsComponent,
        PropertyClassesComponent,
        PropertyClassLangsComponent,
        SaftMainGroupsComponent,
        SaftMainGroupComponent,
        OrderStatusesComponent,
        OrderStatusLangsComponent,
        PurchHeadsComponent,
        UserGroupsComponent,
        UserGroupComponent,
        UserGroupViewComponent,
        SeasonalitiesComponent,
        SeasonalityComponent,
        SopsComponent,
        SopMenuComponent,
        SopComponent,
        ServiceObjectTypesComponent,
        ServiceObjectTypeMenuComponent,
        CustomerEditComponent,
        PostplaceComponent,
        PostplaceViewComponent,
        ServiceObjectTypeComponent,
        ServiceObjectTypeIntsComponent,
        SkillsCertificateTypesComponent,
        InputPhoneComponent,
        InputEmailComponent,
        ProjectFoldersComponent,
        ProjectFoldersViewComponent,
        ProjectFolderComponent,
        ProjectProcessComponent,
        ProjectProcessViewComponent,
        SelectGlyphiconComponent,
        P2StatSourceComponent,
        P2StatSourceEditComponent,
        StockCorrCodesComponent,
        StockCorrCodeComponent,
        StockCorrCodeLangsComponent,
        StockZoneMenuComponent,
        StockZoneComponent,
        StockLocationComponent,
        P2TerminalsComponent,
        P2TerminalComponent,
        PaymentsComponent,
        PaymentComponent,
        PaymentLangsComponent,
        CoopsComponent,
        CoopComponent,
        CoopLangsComponent,
        TravExpTypeMenuComponent,
        TravExpTypesComponent,
        TravelWageSetsComponent,
        TravelWageSetMenuComponent,
        TravelWageSetComponent,
        UnitsComponent,
        UnitLangComponent,
        AcVatCodesComponent,
        AcVatCodeComponent,
        AcVatCodeDataComponent,
        DomObjectTypesComponent,
        DomObjectTypeMenuComponent,
        DomObjectTypePropComponent,
        DomObjectTypeComponent,
        SelectLineModelComponent,
        SelectLineModelViewComponent,
        SelectLineProductsComponent,
        SelectLineProductsViewComponent,
        MultipleProductsConfigDialogComponent,
        PostPlacesComponent,
        AccountingAllocationsComponent,
        AccountingAllocationComponent,
        AccountingAllocationLineComponent,
        CustomerEditViewComponent,
        GuardListComponent,
        CheckInMenuComponent,
        CheckInStatusComponent,
        PowtimesGridComponent,
        P2OffDutyDatasComponent,
        PayCheckBatchesComponent,
        PowTimeApprovalsComponent,
        SalaryTypesComponent,
        SalaryTypeLangComponent,
        LabourSalaryTypesComponent,
        LabourSalaryTypeComponent,
        OffDutyCodesComponent,
        OffDutyCodeComponent,
        HolidaysViewComponent,
        HolidaysComponent,
        HolidayComponent,
        SubscriptionGridComponent,
        ServiceObjectsComponent,
        ServiceObjectComponent,
        FindPersonComponent,
        CustomerPersonsComponent,
        CustomerDiscGrpProdComponent,
        CheckListDatasComponent,
        SelectCheckListAddComponent,
        CustFactsComponent,
        ActivityGridComponent,
        CustactDeadLinesComponent,
        RfidSearchComponent,
        IncomingCallComponent,
        VisitorComponent,
        WebsitesComponent,
        MetaTableDatasComponent,
        SelectArticlesComponent,
        DoDataImpComponent,
        DoDataImpImportDialogComponent,
        DoDataImpViewComponent,
        InfoScreenComponent,
        EmailArchiveDialogComponent,
        EmailPersonInfoDialogComponent,
        EmailPersonInfoFormComponent,
        EmailMoveToFolderComponent,
        PurchPlanningComponent,
        StockGetComponent,
        StockOnLocationComponent,
        StockAllComponent,
        StockIncomingComponent,
        LookupMultiCellEditorComponent,
        CreatePoPuComponent,
        StockHistoryComponent,
        PohTopComponent,
        ChipCellRendererComponent,
        OrderlineCalcFormComponent,
        OrderlineCalcFormViewComponent,
        PohComponent,
        StockOutgoingComponent,
        ProductPricesComponent,
        PriceComponent,
        PriceListFactorsComponent,
        ProductionRoutingCompComponent,
        ProductionRoutingCompViewComponent,
        ComponentsComponent,
        ComponentAlternativesComponent,
        ComponentAlternativeComponent,
        ProductPicsComponent,
        ProductPicComponent,
        ChoicesComponent,
        ChoiceComponent,
        ChoicePossibilityListComponent,
        AcLedgerViewComponent,
        ProdSizesComponent,
        ProdSizeEditComponent,
        ProdSizeColliComponent,
        ProductLinksComponent,
        ProductLinkComponent,
        DoDataImpsWizardEditComponent,
        ProductLinksChangeComponent,
        ProductMultiUnitComponent,
        ProductUnitsComponent,
        ProductUnitComponent,
        ProductUnitsEditComponent,
        ProductUnitDataComponent,
        ProductEansComponent,
        ChipFloatingFilterComponent,
        ChipFilterComponent,
        GridToolbarComponent,
        GridButtonComponent,
        EdgeRelationsComponent,
        EdgeRelationComponent,
        SelectableProductsComponent,
        CalcCopyFromComponent,
        CalcCopyFromViewComponent,
        ComponentReplacementsComponent,
        ProductWasteComponent,
        PriceListsComponent,
        PriceListComponent,
        ProjectProgressLogComponent,
        ProjectProgressLogViewComponent,
        PriceListBusCosComponent,
        PriceListBusCoComponent,
        CollectionsComponent,
        CollectionMenuComponent,
        MaterialGradesComponent,
        MaterialGradesViewComponent,
        CertificateRequirementsComponent,
        CertificateRequirementsViewComponent,
        UltraSonicClassesComponent,
        UltraSonicClassesViewComponent,
        MaterialGradeComponent,
        MaterialGradeViewComponent,
        CollectionLangsComponent,
        CollectionComponent,
        ProductLangsComponent,
        ProductLangComponent,
        PrepareBomsComponent,
        TextileStockSearchComponent,
        StockBatchSearchComponent,
        StockBatchCorrQtyComponent,
        StockCorrectionsComponent,
        ChecklistDataComponent,
        ChecklistDataViewComponent,
        InputSignatureComponent,
        StockHistoricComponent,
        DefaultCountersComponent,
        StockControlSpListComponent,
        BusinessCoDefaultCountersComponent,
        BusinessCoDefaultCounterComponent,
        StepperComponent,
        StepComponent,
        CalViewComponent,
        CalViewViewComponent,
        StockCountsComponent,
        StockCountMenuComponent,
        StockCountDataComponent,
        StockCountDatasComponent,
        ChargesComponent,
        StockCountRefreshComponent,
        StockCountComponent,
        ChargeComponent,
        ChargeViewComponent,
        ChargeTestComponent,
        ChargeTestViewComponent,
        StockCountResultComponent,
        StockTransfersComponent,
        StockTransferCreateComponent,
        RequisitionsComponent,
        RequisitionComponent,
        OrderPickNewComponent,
        OrderPickNewViewComponent,
        OrderPickViewComponent,
        OrderPickViewViewComponent,
        CuttingPlansComponent,
        PoLoadPlanComponent,
        PoComponent,
        OhMaterialCertificatesComponent,
        MatcertCreateComponent,
        MatcertComponent,
        ShopFloorSelectOhComponent,
        GridToggleComponent,
        GridToggleStateComponent,
        DynamicDatataskGotoComponent,
        EmailFolderTreeDataSourceDirective,
        EmailMailFoldersComponent,
        RequisitionPlanViewComponent,
        RequisitionCreateComponent,
        StockTransferLogComponent,
        OeeStatesComponent,
        OeeStateEditComponent,
        EquipmentsComponent,
        QuickAccessComponent,
        AppBreadcrumbsComponent,
        BreadcrumbsComponent,
        BreadcrumbItemComponent,
        LabourPositionsComponent,
        MaterialItemsComponent,
        OperationEquipmentsComponent,
        PaperlessProductionComponent,
        PaperlessPoComponent,
        OrderLineCreditsComponent,
        MaterialItemComponent,
        MaterialItemViewComponent,
        StockBatchLoggComponent,
        OrderLineCreditComponent,
        ConsignmentShipmentsComponent,
        ConsignmentShipmentComponent,
        PickJobGrpBatchesPickerComponent,
        PickJobGrpBatchesComponent,
        SubtasksComponent,
        MaterialItemPickComponent,
        // ConsignmentBatchConsignmentsComponent,
        RequisitionsBetweenComponent,
        TravelBillsComponent,
        DocPackTypeFormatComponent,
        DocPackTypeViewComponent,
        DocPackTypeChaptersComponent,
        DocPackComponent,
        DocPackChapterComponent,
        TravelWageDataComponent,
        TravelBillMenuComponent,
        AcCustTransesAgeComponent,
        VatReportsComponent,
        VatReportComponent,
        VatSpesificationComponent,
        ReceivablesComponent,
        BankReconciliationsComponent,
        CollectionCalculationComponent,
        OrderHeadComponent,
        OrderLinesGridComponent,
        OrderHeadCancelOrderlinesDialogComponent,
        InterbranchInvoicingComponent,
        InterbranchInvoicingMiComponent,
        CustomerInvoicingsComponent,
        CustomerInvoicingComponent,
        DataMiningListComponent,
        PagerComponent,
        DataMiningTableComponent,
        AccountingBuntsComponent,
        FactoringBuntsComponent,
        PurchHeadCreateComponent,
        SelectProjectComponent,
        SelectProjectViewComponent,
        FactoringBuntComponent,
        ProjectInvoicingsComponent,
        ProjectInvoicingComponent,
        TimeInOutsComponent,
        PowTimePayCheckLsComponent,
        DeviationsComponent,
        DeviationComponent,
        CustactMembersComponent,
        CustactCustomersComponent,
        CustactCustomerComponent,
        BiReportsComponent,
        AcSuppTransesAgeComponent,
        AcSuppTransComponent,
        DoDataImpsComponent,
        PurchHeadReceiveSimplifiedComponent,
        ShopFloorComponent,
        CustGrp2sComponent,
        CustGrp2Component,
        CustGrp2ViewComponent,
        DataImpsComponent,
        DataImpComponent,
        GridExportsComponent,
        P2ExportProcsComponent,
        P2ExportProcMenuComponent,
        P2ExportProcUserGroupsComponent,
        P2ImportProcsComponent,
        GanttComponent,
        GanttToolbarComponent,
        P2ImportProcComponent,
        JobSchedulesComponent,
        JobQueueMonitorComponent,
        DocArticleSearchComponent,
        EdiImportsComponent,
        EdiExportsComponent,
        EdiSourcesComponent,
        EdiDestinationsComponent,
        TimeSheetsComponent,
        BspsComponent,
        MieTablesComponent,
        MsgCreateComponent,
        MenuGrpsComponent,
        BatchDeliveriesComponent,
        BatchDelPurchLineComponent,
        BatchDelPurchLinesBsComponent,
        BatchDeliveryAddComponent,
        BatchDeliveryListComponent,
        BatchDelReceiveLinesComponent,
        BatchDelControlComponent,
        BdScanDetailComponent,
        BatchDelReceiveLineComponent,
        BusinessCosComponent,
        CheckListsComponent,
        CheckPointsComponent,
        CheckPointHeadsComponent,
        ColorGrpsComponent,
        ColorGrpMenuComponent,
        ColorGrpLangComponent,
        ColorGrpComponent,
        ConsignmentLineComponent,
        ConsignmentLinesBsComponent,
        ConsignmentLinesBComponent,
        ConsignmentLinesComponent,
        ConsignmentOlComponent,
        CustactWorksComponent,
        CustactMemberEditComponent,
        CustomerBusCosComponent,
        CustomerBusCoComponent,
        OrderHeadsComponent,
        DataMiningCreateComponent,
        DeliveryCustomersComponent,
        DeliveryCustomerComponent,
        DeviationTypeMenuComponent,
        DeviationTypeLangsComponent,
        EdiDestinationComponent,
        EdiSourceComponent,
        ExternalApisComponent,
        CuttingPlanPoAddComponent,
        ExternalApiComponent,
        FreightRateSetsComponent,
        FreightRateComponent,
        InvoiceMakeMultiOrdersComponent,
        InvoiceMakeOverviewComponent,
        LabourJobNoteComponent,
        OhDirectDeliveryComponent,
        OhDispatchesComponent,
        OrderHeadCalcsComponent,
        OrderDeliveryInfoComponent,
        OrderHeadSetDiscComponent,
        OrderLineMsComponent,
        OrderLineStatusComponent,
        OrderPurchMenuComponent,
        PoMenuComponent,
        PortalUserSignatureComponent,
        PositionsComponent,
        PositionComponent,
        PrepaymentsComponent,
        PrepaymentComponent,
        PrepickCreateComponent,
        SelectShiftComponent,
        SelectOperationPuComponent,
        SelectPortalUserComponent,
        SelectEquipmentPuComponent,
        CuttingPlanComponent,
        OrderHeadsFilterComponent,
        OrderHeadsPagedComponent,
        CustactEditComponent,
        WorkDetailsComponent,
        CuttingPlanRealComponent,
        WorkDetailsRegisterFunctionsComponent,
        WorkDetailsPlanFunctionsComponent,
        WorkDetailsDesktopComponent,
        WorkDetailsMobileComponent,
        WorkDetailsPlansFunctionsComponent,
        RelatedPurchOrdersComponent,
        RequisitionCreateFromComponent,
        ProjectProcessesComponent,
        ProductViewComponent,
        ServiceObjectNewActComponent,
        ShipmentContainersComponent,
        ShipmentContainerComponent,
        SopClsComponent,
        AccountingProcessingCarouselComponent,
        AccountingProcessingListComponent,
        SopClComponent,
        AccountingProcessingCarouselComponent,
        AccountingProcessingListComponent,
        CustomersMenuComponent,
        ProjectsGridComponent,
        ActivitiesGridComponent,
        StockBatchListComponent,
        StockStockBatchAssignedComponent,
        StockTransAllocationsComponent,
        OrderHeadsGridComponent,
        CustomersMenuFunctionsComponent,
        StockTransAllocationsComponent,
        StockTransMenuComponent,
        StockVariantsComponent,
        SubscriptionsComponent,
        TimeCustomersComponent,
        TimeCustomersWoComponent,
        PersonComponent,
        CustomerClaimsComponent,
        TimeProjectsComponent,
        TimeSheetPowTimeListComponent,
        TravelWagesComponent,
        ValutasComponent,
        AccountingAllocationBasisComponent,
        VisitorConfirmComponent,
        UnitComponent,
        TravExpTypeComponent,
        OrderHeadMiComponent,
        WebPageSubscriptionComponent,
        WebPageLinkComponent,
        P2WebpageTableComponent,
        TrMenuComponent,
        WebpageTableEditComponent,
        TrNewJobSelectOpComponent,
        TravelBillRejectComponent,
        SopValidComponent,
        SelectTextileModelComponent,
        SelectStockLocationComponent,
        MultiDocComponent,
        DeviationTypesComponent,
        CustactCalcAdjustDiscComponent,
        CuttingplansChangeComponentComponent,
        CustactCalcPickSelectComponent,
        CheckListItemItemsComponent,
        CheckListItemItemComponent,
        CargonizerPrintersComponent,
        CargonizerPrinterEditComponent,
        ConsignmentLineSelectComponent,
        ConsignmentSplitLineComponent,
        CustGrpComponent,
        FreightRateSetCreateComponent,
        FinishPohScanComponent,
        ActivityTypeComponent,
        ActStatusComponent,
        BusinessCoSettingCargonizerComponent,
        CategoryLinkAddComponent,
        TrNewJobConfComponent,
        OrderDialogComponent,
        OrderHeadCopyComponent,
        CheckInComponent,
        ChoiceProdPriceGrpsComponent,
        ChoiceProdPriceGrpComponent,
        ConsignmentColliDetailComponent,
        FurnitureFabricsComponent,
        OeeStateLogComponent,
        OrderHeadCreateIbPurchComponent,
        PoFinishComponent,
        PoFinishProdListComponent,
        PoFinishProdWorkComponent,
        PoFinishProdWorksComponent,
        PohCompAddScanComponent,
        PohCompAddComponent,
        PoFinishProdCompsBsComponent,
        PoFinishProdCompsBComponent,
        PoFinishProdCompListAccComponent,
        PoFinishPoFinishCompsComponent,
        PoFinishProdCompComponent,
        PoFinishProdCompQtyComponent,
        StockBatchGetComponent,
        TracesBComponent,
        SchedulerComponent,
        TimeSheetDiaryComponent,
        StockLocationCtrlComponent,
        DocArticleTopUserGroupsComponent,
        DocArticleTopUserGroupComponent,
        DocArticlePorusComponent,
        ECommerceStatusComponent,
        InventoryArticleSearchComponent,
        GisPositionAddComponent,
        GisPositionComponent,
        OlmRequisitionComponent,
        P2OffDutyDataComponent,
        SubOperationsComponent,
        SubOperationComponent,
        SubOperationLangComponent,
        TraceSearchComponent,
        TimeProjectsWoComponent,
        TimeRegisterSplitComponent,
        TimeSheetsStatusComponent,
        TravelBillLogComponent,
        TravelBillLogsComponent,
        TravelExpenceComponent,
        TravelWageComponent,
        WorkingOrdersComponent,
        BusinessCoComponent,
        ProductSettingComponent,
        ProductSearchComponent,
        ProductSettingMcsFormComponent,
        ProductSettingMcsFormInputComponent,
        ProductCreateComponent,
        ProductComponent,
        ProductFunctionsComponent,
        CheckListComponent,
        HelpDocTtChangedComponent,
        CustactWorkComponent,
        PohKeynoTopStatusComponent,
        PostCalcComponent,
        PostCalcMComponent,
        ProductionStatusComponent,
        ConsignmentDeliveryComponent,
        ConsignmentPlansDeliverySettingsComponent,
        ConsignmentScanComponent,
        ConsignmentPlansDeliveryDriverComponent,
        ConsignmentPlansDeliveryLabourComponent,
        ConsignmentPlansDeliveryLocationComponent,
        ConsignmentSplitComponent,
        ProductBasicFormComponent,
        ProductKpiFormComponent,
        ProductStockFormComponent,
        ProductPimFormComponent,
        ProductPurchaseFormComponent,
        ProductProductionFormComponent,
        ProductEditComponent,
        ProductEditSaveComponent,
        WebpageSettingComponent,
        WebpageSettingMenuComponent,
        IeHttpSrcDirective,
        ImageSrcDirective,
        ConsignmentSplitEditComponent,
        ConsignmentCarrierOneComponent,
        ComponentReplaceAblesComponent,
        CustProdAccBcsComponent,
        AcLedgerComponent,
        AcCustTransComponent,
        SkillsCertificatesComponent,
        SkillsCertificateComponent,
        OrderHeadInfoFormComponent,
        OrderHeadEditComponent,
        OrderHeadAddressFormComponent,
        OrderHeadCustomerAddressFormComponent,
        OrderHeadDeliveryAddressFormComponent,
        OrderHeadInvoiceAddressFormComponent,
        OrderHeadDeliveryFormComponent,
        OrderHeadPriceFormComponent,
        SkillsCertificateTableComponent,
        SkillsCertificateTypeComponent,
        TimeStampStartCorrComponent,
        AcReminderProposalComponent,
        AccessInfoComponent,
        HelpDocSearchComponent,
        EdiImportFileUploadComponent,
        P2SubscriptionsEditComponent,
        P2ClientUpdateComponent,
        P2ClientUpdateEditComponent,
        MenuGrpComponent,
        SearchStockComponent,
        CountersComponent,
        CounterComponent,
        PostCalcWComponent,
        FurnitureFabricsSuppliersComponent,
        DepartmentComponent,
        BulkMailsComponent,
        BulkMailComponent,
        TextileModelCategory3sComponent,
        TextileModelCategory3Component,
        TextileModelCategory3LangComponent,
        ListsComponent,
        OrderHeadCustomerFormComponent,
        OrderHeadFunctionsComponent,
        OrderHeadProductionComponent,
        OrderHeadHoursComponent,
        OrderHeadOrderPicksComponent,
        OrderHeadDeliveryComponent,
        ListDatasComponent,
        ListDataComponent,
        ListDataLangComponent,
        ListNameComponent,
        GridMessagesModalComponent,
        OrderHeadEmailsComponent,
        OrderHeadCustactAcComponent,
        OrderHeadDynamicPostCalcComponent,
        OrderHeadConvertToOrderButtonComponent,
        OrderHeadCancelDialogComponent,
        PoFinishProdCompListAccsComponent,
        SelectNewDocTypeComponent,
        WebSiteNewComponent,
        PurchRequestCreateComponent,
        RequisitionSimplifiedComponent,
        RequisitionSimplifiedFiltersComponent,
        OrderHeadChoicesComponent,
        OrderHeadChoiceComponent,
        OrderHeadCreateProjectComponent,
        ProjectActivityTypesComponent,
        ProjectActivityTypeComponent,
        OrderCustComComponent,
        OeeDashBoardComponent,
        EquipmentEditComponent,
        CustactPlansComponent,
        CategoryLinksComponent,
        CheckListItemTwsComponent,
        CheckListItemTwComponent,
        CustactCalcPickComponent,
        OrderHeadOrderLineCalculationComponent,
        OrderHeadOrderLinesComponent,
        DeliveryMethodComponent,
        DispatchPlanningBlockOrderComponent,
        EdiExportComponent,
        EdiImportComponent,
        InterBranchInvoiceLineComponent,
        InvoiceHeadEhfComponent,
        InvoiceMakeMultiComponent,
        JobScheduleComponent,
        InvoiceHeadComponent,
        SelectReportComponent,
        ProductMoveChoicesComponent,
        ProductMoveChoiceComponent,
        ProductDataHarvestComponent,
        ComponentComponent,
        SellerComponent,
        SellersComponent,
        ComponentCalculationComponent as Type<unknown>,
        ComponentCalculationGridModalComponent as Type<unknown>,
        ProductionRoutingAddComponent,
        CategoryDatasComponent,
        CategoryDataSelectComponent,
        BrRegComponent,
        StockStatusComponent,
        StockStatusDrillDownComponent,
        TransportAreaPpMoveComponent,
        BrRegViewComponent,
        PoJobLaboursComponent,
        AiChatComposerComponent,
        AiChatComponent,
        AiChatConversationListComponent,
        AiChatConversationComponent,
        AiChatMessageComponent,
        AiChatHeaderComponent,
        AiChatPanelComponent,
        AiChatThinkingComponent,
        AiChatToolCallsDialogComponent,
        AiChatWorkspaceComponent,
        AccountingBuntComponent,
        CalculationSeasonYearComponent,
        CalculationOverviewComponent,
        DomObjectComponent,
        ChoicePossRuleComponent,
        ChoicePossRulesComponent,
        ChoicePossRulesRunComponent,
        BankReconciliationAnalysisComponent,
        BusinessCoOnBoardingComponent,
        CampaignsComponent,
        CampaignEditComponent,
        CampaignProductsComponent,
        BusinessCoAccountingComponent,
        AcVoucherTypesComponent,
        AcRemLtrComponent,
        BusinessCoBankAccountComponent,
        ForecastDataGridComponent,
        SpecialCustProdCalcComponent,
        FindCustomersComponent,
        FindCustomersGridComponent,
        FindCustomersListComponent,
        CsvExportArgComponent,
        CsvExportsComponent,
        CustactCalcJobExitComponent,
        PohTopOpComponent,
        PoPurchPlanningComponent,
        AcCustTransesComponent,
        AcSuppTransesComponent,
        PoConnectedPositionsComponent,
        PicturesComponent,
        PictureCardComponent,
        DocArcComponent,
        DocArcInformationComponent,
        PicturesListComponent,
        OrderHeadCapJobComponent,
        AcReportLayoutMenuComponent,
        P2ExportProcComponent,
        P2ReleaseNoteComponent,
        P2ReleaseNoteEditComponent,
        P2TableComponent,
        P2TableEditComponent,
        P2UpdateTablesComponent,
        P2UpdateTableEditComponent,
        P2UpdateProceduresComponent,
        P2UpdateProcedureEditComponent,
        P2WebpageComponent,
        P2WebpageEditComponent,
        P2WebpageLinkEditComponent,
        P2DataTaskComponent,
        P2DataTaskEditComponent,
        P2SubscriptionComponent,
        P2SubscriptionEditComponent,
        P2ReportDefComponent,
        P2ReportDefsComponent,
        P2NotificationsComponent,
        NiComponent,
        BspComponent,
        BspMenuComponent,
        BspSetupComponent,
        BspSubComponent,
        BspUgComponent,
        CarrierComponent,
        CarriersComponent,
        VatReportMenuComponent,
        VatReportSubmissionStepsComponent,
        CsvImportComponent,
        DiaryComponent,
        DataMiningComponent,
        GanttButtonComponent,
        GanttToggleComponent,
        CapTasksViewComponent,
        CapTaskResourceComponent,
        CaseComponent,
        VtComponent,
        VtLinkComponent,
        VtsComponent,
        WorkComponent,
        SalaryTypeComponent,
        TravelBillComponent,
        TranslateComponent,
        TranslateEditPageComponent,
        TempstockComponent,
        ShiftComponent,
        ShiftDayComponent,
        ShiftDayBreakComponent,
        ShiftDayTimeComponent,
        ShiftPlanComponent,
        ShiftPlansComponent,
        ShiftsComponent,
        ShiftViewComponent,
        SrCrewComponent,
        SrCrewMenuComponent,
        SrCrewResourceComponent,
        SrCrewResourceViewComponent,
        SrCrewsComponent,
        SrCheckListCrewChangeComponent,
        SrCheckListCrewsComponent,
        SrCheckListEquipmentComponent,
        SrCheckListEquipmentsComponent,
        SrCheckListExpenseComponent,
        SrCheckListExpensesComponent,
        SrCheckListLdComponent,
        SrCheckListLdsComponent,
        SrOperationComponent,
        SrOperationAddrComponent,
        SrOperationArComponent,
        SrOperationManualAddComponent,
        SrOperationResourceComponent,
        SrOperationsComponent,
        FossProductionComponent,
        FactsComponent,
        FactseditComponent,
        EdiMenuComponent,
        EdiOrderHeadsComponent,
        EdiSetupComponent,
        EdiSetupModalComponent,
        CustactGanttComponent,
        GenericTerminalScanAComponent,
        ExcelImportComponent,
        GridExportArgComponent,
        OeeDataComponent,
        OeeDataCurrentComponent,
        OeeMenuComponent,
        SelectOeeStateComponent,
        SelectOeeStateRemarkComponent,
        SelectLabourPuComponent,
        SelectLabourComponent,
        SelectCityComponent,
        SelectCustomerComponent,
        SelectWorkingOrderComponent,
        SelectProdChoiceComponent,
        SelectProdsizeComponent,
        SelectPoFinishComponent,
        SelectProductComponent,
        UsersComponent,
        ShipmentComponent,
        RiskElementComponent,
        RiskElementsComponent,
        ConsignmentsComponent,
        ConsignmentSerialNumberComponent,
        ConsignmentLineAssignComponent,
        SrUserComponent,
        SrUserAdminComponent,
        SrUsersComponent,
        SrEquipmentComponent,
        SrEquipmentsComponent,
        SrEquipmentResourceViewComponent,
        SrResourceComponent,
        SrResourceViewComponent,
        SrFollowUpComponent,
        SrFollowUpForwardComponent,
        SrFollowUpsComponent,
        SrNotCompletedComponent,
        SrNotCompletedNewComponent,
        SrRefundBasisComponent,
        SrExpensesComponent,
        SrTravelBillComponent,
        SrTravelBillSpecComponent,
        SrReportMenuComponent,
        SrActivityComponent,
        SrFindReportComponent,
        UsergroupSettingComponent,
        UsergroupSettingEditComponent,
        UsergroupTableComponent,
        UsergroupTableEditComponent,
        SrRefundCreateComponent,
        SrRefundInputCrewComponent,
        SrCustomerEditComponent,
        SrRefundListComponent,
        CustomerBonusComponent,
        WebpageSettingEditComponent,
        WebpageSubscriptionEditComponent,
        ConsignmentComponent,
        AiSettingsComponent,
        AiSettingsProvidersSetupComponent,
        AiSettingsModelsComponent,
    ],
    imports: [
        BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
        HttpClientModule,
        UpgradeModule,
        UIRouterUpgradeModule.forRoot(),
        AppConfigModule,
        MicrosoftGraphModule,
        BrowserAnimationsModule,
        MatDialogModule,
        TtInputComponent,
        FormsModule,
        NgChartsModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatButtonModule,
        MutliSelectFilter,
        MatTooltipModule,
        MatPaginatorModule,
        AgGridModule,
        AgChartsAngularModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        MatInputModule,
        MatChipsModule,
        DragDropModule,
        OverlayModule,
        MatTableModule,
        MatSidenavModule,
        GridstackModule,
        TextFieldModule,
        CdkScrollableModule,
        TtListLegacyModule,
        EditorModule,
        PopupModule,
        DialogModule,
        InputsModule,
        ButtonsModule,
        LabelModule,
        ScrollingModule,
        TreeViewModule,
        SpreadsheetModule,
        YouTubePlayerModule,
        SchedulerModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        AppConfigService,
        GraphAuthService,
        CalendarService,
        GraphUserService,
        GraphMailService,
        GoogleTranslateService,
        DataTaskService,
        ModalService,
        HtmlReportService,
        ResponsiveService,
        ResponsiveBridgeService,
        UserService,
        UserStore,
        NativeDateAdapter,
        PrintService,
        {
            provide: MSAL_INSTANCE,
            useFactory: () => {
                return new PublicClientApplication({
                    auth: {
                        clientId: 'c90067f7-0ccf-43ee-a05c-30b601f5e721', //,
                        //redirectUri: 'https://hybridtest.touchtime.app/index.html',
                        //postLogoutRedirectUri: 'https://hybridtest.touchtime.app/index.html'
                    },
                    cache: {
                        cacheLocation: 'localStorage', // Change from "sessionStorage" to "localStorage"
                        storeAuthStateInCookie: true, // Recommended for IE11/Edge
                    },
                });
            },
        },
        MsalService,

        // {
        //     provide: POPUP_CONTAINER,
        //     useFactory: () => {
        //         //return the container ElementRef, where the popup will be injected
        //         return { nativeElement: document.body };
        //     },
        // },
        {
            provide: POPUP_CONTAINER,
            useFactory: () => {
                //return the container ElementRef, where the popup will be injected
                return { nativeElement: document.body } as ElementRef;
            },
        },
        { provide: DateAdapter, useClass: TTDateAdapter },
        { provide: MatPaginatorIntl, useClass: PaginatorIntlService },
        { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
        {
            provide: MAT_CHIPS_DEFAULT_OPTIONS,
            useValue: {
                separatorKeyCodes: [ENTER, 188],
            },
        },
        { provide: LOCALE_ID, useValue: 'en' },
        {
            provide: APP_INITIALIZER,
            useFactory: (userStore: UserStore, dataTask: DataTaskService, appSettings: AppSettingsService, modal: ModalService) => async () => {
                await appSettings.ensureIsReady();
                await dataTask.init(modal);
                await userStore.init(appSettings);
            },
            deps: [UserStore, DataTaskService, AppSettingsService, ModalService],
            multi: true,
        },
        // {
        //     provide: '$injector',
        //     useFactory: () => (window as any).angular.element(document.body).injector(),
        // },
        {
            provide: '$scope',
            useExisting: '$rootScope',
        },
        {
            provide: 'printService',
            useFactory: (i: any) => i.get('printService'),
            deps: ['$injector'],
        },
        {
            provide: 'fileService',
            useFactory: (i: any) => i.get('fileService'),
            deps: ['$injector'],
        },
        {
            provide: 'stateService',
            useFactory: (i: any) => i.get('stateService'),
            deps: ['$injector'],
        },
        {
            provide: '$stateParams',
            useFactory: (i: any) => i.get('$stateParams'),
            deps: ['$injector'],
        },
        {
            provide: 'ab-base64',
            useFactory: (i: any) => i.get('base64'), // JLR 20250611 - base64 urlencoding/decoding available in ts till now doesnt handle emojis, but this old angularjs constant does.
            deps: ['$injector'],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CommonInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
    ],
    entryComponents: [WordlangDialogComponent],
})
export class AppModule {
    constructor(private upgrade: UpgradeModule) {
        GridstackComponent.addComponentToSelectorType([WebpagemenuGroupWidgetComponent, WebpagemenuDashboardWidgetComponent, DashboardWidgetBaseComponent, DashboardSlicerWidgetBaseComponent, MyPageCustomWidgetComponent]);
    }

    ngDoBootstrap() {
        this.upgrade.bootstrap(document.documentElement, [imAppModule.name], { strictDi: true });
    }
}
